import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLatest } from "@/hooks/useLatest";
import { formatDateShort, formatDateLong } from "@/lib/formatDate";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import { useResolvedConfig } from "@/context/ResolvedConfigContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import {
    Plus, Upload, FileText, Sparkles, Check, Paperclip,
    Trash2, Save, Eye, Camera, ScanLine, ArrowRight, ArrowLeft,
    X as XIcon, AlertTriangle, Boxes, ChevronUp, ChevronDown,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import PhoneCompanionModal from "@/components/PhoneCompanionModal";
import { Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiErrorMessage, translateErrorText } from "@/lib/apiErrorMessage";

const STATUS_TONE = {
    completed: "completed", reviewed: "completed",
    needs_review: "action_required", uploaded: "pending",
};

const STATUS_LABEL = {
    completed: "Completed", reviewed: "Reviewed",
    needs_review: "Needs Review", uploaded: "Draft",
};

// Prompt 45 · package-like units (chef/AI wrote e.g. "1 box" with no
// physical size). We MUST NOT invent kg/L for these — mark
// review_required so the stock write is deferred until the chef fixes
// pack_count / pack_size / pack_unit.
const _AMBIGUOUS_PACKAGE_UNITS = new Set([
    "box", "boxes", "case", "cases", "sack", "sacks", "bag", "bags",
    "package", "packages", "pkg", "pack", "packs",
    // Finnish equivalents commonly seen on FI invoices.
    "ltk", "laatikko", "säkki", "sakki", "pussi",
]);

function _isAmbiguousPackage(unit, packCount, packSize, packUnit) {
    const u = String(unit || "").trim().toLowerCase();
    if (!_AMBIGUOUS_PACKAGE_UNITS.has(u)) return false;
    const hasPack = Number(packCount) > 0
        && Number(packSize) > 0
        && String(packUnit || "").trim();
    return !hasPack;
}

// Stable presentation (size + unit) comes from the document or the
// remembered package; the received COUNT always comes from the current
// delivery. Physical-measurement lines (kg / l …) never get a count.
const _PHYSICAL_UNITS = new Set(["kg", "g", "mg", "l", "ml", "cl", "dl"]);
function _currentPackCount(qty, unit, packCount, packSize, packUnit) {
    if (Number(packCount) > 0) return packCount;
    if (!(Number(packSize) > 0) || !String(packUnit || "").trim()) return packCount ?? null;
    if (_PHYSICAL_UNITS.has(String(unit || "").trim().toLowerCase())) return packCount ?? null;
    return Number(qty) > 0 ? Number(qty) : (packCount ?? null);
}

// Shared per-line NET: printed line total when still valid, otherwise
// qty × unit price. Mirrors the server's canonical recompute.
const _round2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;
function _lineNet(it) {
    return (it?.line_price != null && it.line_price !== "" && !it.line_price_invalidated)
        ? _round2(Number(it.line_price) || 0)
        : _round2((Number(it?.quantity) || 0) * (Number(it?.price) || 0));
}

// Prompt 45 · derived physical shipment shown next to the pack line.
// Returns null when the delivery is a direct (kg/L/pcs) quantity —
// callers then fall back to displaying "Received: {qty} {unit}".
function _derivedReceived(it) {
    const pc = Number(it.pack_count);
    const ps = Number(it.pack_size);
    const pu = String(it.pack_unit || "").trim();
    if (pc > 0 && ps > 0 && pu) {
        return { qty: pc * ps, unit: pu };
    }
    return null;
}

// Prompt 45 · normalized €/received_unit for the review UI. Returns
// null when either the price or the received qty is unavailable so
// the caller can render "—".
function _normalizedUnitCost(it) {
    const rec = _derivedReceived(it);
    const qty = Number(it.quantity) || 0;
    const price = Number(it.price) || 0;
    const lineTotal = qty * price;
    if (!rec || rec.qty <= 0 || lineTotal <= 0) return null;
    return { value: lineTotal / rec.qty, unit: rec.unit };
}


export default function Deliveries() {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const { scopedParams, withRestaurant } = useRestaurantScope();
    const { isFeatureEnabled } = useResolvedConfig();
    const companionEnabled = isFeatureEnabled("phone_companion_capture");
    const [deliveries, setDeliveries] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    // Both Connect Phone entry points open the same page-collection wizard.
    const [startWithPhone, setStartWithPhone] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);

    const params = scopedParams;

    const load = useCallback(async () => {
        if (!restaurantId) return;
        const [{ data: d }, { data: s }] = await Promise.all([
            api.get("/deliveries", { params }),
            api.get("/suppliers", { params }),
        ]);
        setDeliveries(d);
        setSuppliers(s);
    }, [restaurantId, params]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (id) => {
        setSelectedId(id);
        const { data } = await api.get(`/deliveries/${id}`);
        setDetail(data);
    };

    const reloadDetail = async () => {
        if (!selectedId) return;
        const { data } = await api.get(`/deliveries/${selectedId}`);
        setDetail(data);
    };

    const closeDetail = () => { setSelectedId(null); setDetail(null); load(); };

    return (
        <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
            <PageHeader
                title={t("deliveries.title", "Deliveries")}
                subtitle={t("deliveries.subtitle", "What did we receive?")}
                action={
                    deliveries.length > 0 && (
                        <div className="flex gap-2">
                            {companionEnabled && (
                                <button
                                    data-testid="use-phone-camera-btn"
                                    onClick={() => { setStartWithPhone(true); setCreateOpen(true); }}
                                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
                                >
                                    <Smartphone className="h-4 w-4" /> {t("deliveries.connectPhone", "Connect Phone")}
                                </button>
                            )}
                            <button
                                data-testid="new-delivery-button"
                                onClick={() => setCreateOpen(true)}
                                className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                            >
                                <Plus className="h-4 w-4" /> {t("deliveries.newDelivery", "New Delivery")}
                            </button>
                        </div>
                    )
                }
            />

            {deliveries.length === 0 ? (
                <div className="kop-card p-12 text-center" data-testid="deliveries-empty">
                    <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-neutral-100">
                        <FileText className="h-6 w-6 text-neutral-500" />
                    </div>
                    <div className="mt-5 font-display text-2xl font-extrabold">{t("deliveries.noDeliveriesYet", "No deliveries yet.")}</div>
                    <p className="mt-2 text-sm text-neutral-500 max-w-sm mx-auto">
                        {t("deliveries.uploadOrEnterManually", "Upload a delivery note or enter products manually.")}
                    </p>
                    <button
                        data-testid="add-first-delivery"
                        onClick={() => setCreateOpen(true)}
                        className="mt-6 h-11 px-5 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                    >
                        <Plus className="h-4 w-4" /> {t("deliveries.addFirstDelivery", "Add First Delivery")}
                    </button>
                </div>
            ) : (
                <div className="kop-card overflow-hidden">
                    <table className="w-full text-sm" data-testid="deliveries-table">
                        <thead className="bg-neutral-50">
                            <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                <th className="px-5 py-3 font-semibold">{t("deliveries.col.date", "Date")}</th>
                                <th className="px-5 py-3 font-semibold">{t("deliveries.col.supplier", "Supplier")}</th>
                                <th className="px-5 py-3 font-semibold">{t("deliveries.col.items", "Items")}</th>
                                <th className="px-5 py-3 font-semibold">{t("deliveries.col.doc", "Doc")}</th>
                                <th className="px-5 py-3 font-semibold">{t("deliveries.col.status", "Status")}</th>
                                <th className="px-5 py-3 font-semibold">{t("deliveries.col.total", "Total")}</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                            {deliveries.map((d) => (
                                <tr key={d.id} data-testid={`delivery-row-${d.id}`}>
                                    <td className="px-5 py-3 font-medium">
                                        {formatDateShort(d.delivery_date)}
                                    </td>
                                    <td className="px-5 py-3 text-neutral-700">{d.supplier_name}</td>
                                    <td className="px-5 py-3 text-neutral-600">{d.items_count ?? 0}</td>
                                    <td className="px-5 py-3 text-neutral-500">
                                        {d.has_document
                                            ? <Paperclip className="h-4 w-4 text-emerald-600" />
                                            : <span className="text-neutral-300">—</span>}
                                    </td>
                                    <td className="px-5 py-3">
                                        <StatusBadge status={STATUS_TONE[d.status] || "pending"}>
                                            {STATUS_LABEL[d.status] ? t(`deliveries.status.${d.status}`, STATUS_LABEL[d.status]) : d.status}
                                        </StatusBadge>
                                    </td>
                                    <td className="px-5 py-3">€ {Number(d.total_amount || 0).toFixed(2)}</td>
                                    <td className="px-5 py-3 text-right">
                                        <button
                                            data-testid={`open-delivery-${d.id}`}
                                            onClick={() => openDetail(d.id)}
                                            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50"
                                        ><Eye className="h-3.5 w-3.5" /> {t("deliveries.review", "Review")}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CreateDeliveryDialog
                open={createOpen}
                autoPhone={startWithPhone}
                onClose={() => { setCreateOpen(false); setStartWithPhone(false); }}
                suppliers={suppliers}
                onSaved={() => { setCreateOpen(false); setStartWithPhone(false); load(); }}
                onSavedAndOpen={(id) => {
                    setCreateOpen(false); setStartWithPhone(false); load(); openDetail(id);
                }}
            />

            <DeliveryDetailDialog
                open={!!detail}
                onClose={closeDetail}
                delivery={detail}
                reload={reloadDetail}
            />
        </div>
    );
}

function CreateDeliveryDialog({ open, onClose, suppliers, autoPhone, onSaved, onSavedAndOpen }) {
    const { t } = useTranslation();
    // Prompt 35 — AI Delivery Assistant.
    //   Phase 1: Attach delivery note (photo / upload / scan)
    //   Phase 2: AI is reading — Gemini 3 Flash multimodal extraction runs
    //            in the background and returns supplier + date + items with
    //            match suggestions.
    //   Phase 3: Chef reviews the AI proposal and hits Confirm.
    //   Phase 4: Backend atomically creates supplier (if new) + delivery +
    //            items + attaches the document + updates inventory + saves
    //            learning matches. NOTHING persists before the Confirm click.
    const { user, restaurantId } = useApp();
    const { scopedParams, withRestaurant, needsExplicit } = useRestaurantScope();
    const { isFeatureEnabled } = useResolvedConfig();
    const aiEnabled = isFeatureEnabled("ai_delivery_reader");
    const [phase, setPhase] = useState("attach"); // attach | reading | review | error | manual
    // Latest seed helpers for the open/reading effects (assigned below, after definition).
    const seedersRef = useRef(null);
    // Multi-photo delivery capture. Each entry:
    //   { id, file, status: 'pending'|'reading'|'ok'|'error',
    //     extraction: object|null, error: string|null }
    // Preserves capture order. Single-photo path (photos.length === 1)
    // stays byte-identical to the previous behaviour — one /ai-extract
    // call, one file_ref, no /documents follow-up uploads.
    const [photos, setPhotos] = useState([]);
    // "Group images as one delivery" toggle — OFF by default so every
    // upload action produces a NEW delivery unless the chef explicitly
    // opts in to multi-page grouping. When OFF, adding a second photo
    // triggers a hard reset of the wizard so no state leaks across
    // deliveries.
    // null = not chosen yet (required when more than one page).
    const [groupMode, setGroupMode] = useState(null);
    // Separate mode: the page whose delivery is currently in review.
    const [currentDocId, setCurrentDocId] = useState(null);
    const [phonePending, setPhonePending] = useState(false);
    const phoneSeenRef = useRef(new Set());
    const separateMode = groupMode === false && photos.length > 1;
    // Pages that make up the delivery currently being reviewed.
    const docPhotos = separateMode
        ? photos.filter((p) => p.id === currentDocId) : photos;
    // Internal session token. Regenerated whenever the wizard resets
    // (modal open, groupMode-off second-photo, post-confirm). Used to
    // detect stale async responses that would otherwise contaminate a
    // fresh delivery.
    const [sessionId, setSessionId] = useState(() =>
        (typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID() : `s-${Date.now()}`));
    // Legacy alias for review UI that still displays "Delivery note
    // attached: <name>". Kept in sync with photos.
    const file = photos[0]?.file || null;
    const [extractError, setExtractError] = useState("");
    const [ext, setExt] = useState(null);         // full AI response
    // If true, the user has explicitly acknowledged that some photos
    // failed extraction and chosen to continue without them.
    const [ackContinueWithFailures, setAckContinueWithFailures] = useState(false);
    // Editable review state (kept in sync with `ext`).
    const [supplier, setSupplier] = useState({ id: null, name: "", confidence: 0, create_if_missing: false });
    const [deliveryDate, setDeliveryDate] = useState("");
    const [docNumber, setDocNumber] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [notes, setNotes] = useState("");
    // Message 476 · Delivery fee state. Shape mirrors the backend
    // `_validate_delivery_fee` contract. source ∈ {extracted, manual,
    // explicit_zero, needs_review, none}. Seeded from AI extraction;
    // chef can override before confirm. Never persisted verbatim —
    // the server re-validates on every write.
    const [deliveryFee, setDeliveryFee] = useState({
        net: "", vat: "", vat_rate: "", source: "none", raw_text: "",
    });
    const [items, setItems] = useState([]);
    const [saving, setSaving] = useState(false);
    // In-app confirmation modal for the "inline product creation" warning —
    // replaces `window.confirm` so the flow is scriptable and visually
    // consistent with the rest of the wizard.
    const [pendingNewCount, setPendingNewCount] = useState(0);
    // New Product dialog seed — opens `AdminCreateProductDialog`-style dialog
    // (we render locally to avoid extra imports).
    const [newProductSeed, setNewProductSeed] = useState(null);

    // Seeds review state from an AI extraction payload (same shape as
    // POST /deliveries/ai-extract response). Reused by the phone
    // companion capture flow, which delivers the same object over SSE.
    const seedFromExtraction = (data) => {
        setExt(data);
        const s = data?.supplier_match || {};
        setSupplier({
            id: s.confidence >= 0.75 ? s.id : null,
            name: s.name || (data?.extraction?.supplier?.name || ""),
            confidence: s.confidence || 0,
            create_if_missing: !(s.confidence >= 0.75),
        });
        setDeliveryDate(data?.extraction?.delivery_date || new Date().toISOString().slice(0, 10));
        setDocNumber(data?.extraction?.doc_number || "");
        setInvoiceNumber(data?.extraction?.invoice_number || "");
        setNotes("");
        const defaultVat = data?.extraction?.vat_percent_default ?? 13.5;
        setItems((data?.matched_items || []).map((mi, idx) => {
            const ext = mi.extracted || {};
            const packSize = ext.pack_size ?? null;
            const packUnit = ext.pack_unit || "";
            // Ambiguous-packaging detection (Prompt 45 §D): unit is a
            // package word (box/case/sack/bag/pkg) AND no reliable
            // physical pack info. Chef must confirm before stock write.
            const unit = ext.unit || "kpl";
            const conflict = !!mi.pack_conflict;
            const packCount = conflict ? (ext.pack_count ?? null)
                : _currentPackCount(ext.quantity, unit, ext.pack_count, packSize, packUnit);
            const reviewRequired = conflict
                || _isAmbiguousPackage(unit, packCount, packSize, packUnit);
            return {
                _key: `${Date.now()}-${idx}`,
                _src_idx: Number.isInteger(mi.source_index) ? mi.source_index : null,
                _src_file_ref: data?.file_ref || null,
                _pack_count_auto: !conflict && !(Number(ext.pack_count) > 0) && Number(packCount) > 0,
                _pack_conflict: conflict,
                raw_name: mi.raw_name,
                product_id: mi.match?.confidence >= 0.7 ? mi.match.id : null,
                name: mi.match?.confidence >= 0.7 ? mi.match.name : mi.raw_name,
                match_confidence: mi.match?.confidence || 0,
                match_reason: mi.match?.reason || "none",
                quantity: ext.quantity || 0,
                unit,
                price: ext.unit_price ?? (
                    ext.line_price && ext.quantity
                        ? ext.line_price / ext.quantity
                        : 0),
                vat: ext.vat_percent ?? defaultVat,
                line_price: ext.line_price ?? null,
                line_price_invalidated: false,
                barcode: ext.barcode || "",
                supplier_sku: ext.sku || "",
                // Prompt 45 · structured pack info + free-text package.
                pack_count: packCount,
                pack_size: packSize,
                pack_unit: packUnit,
                package: ext.package || "",
                review_required: reviewRequired,
                new_product: !(mi.match?.confidence >= 0.7),
                ignored: false,
            };
        }));
        setPhase("review");
    };

    // Pre-save validation warnings — never block confirm, only surface
    // possible issues to the chef. Computed from current wizard state
    // whenever items / supplier / invoice change.
    const [dupCheckState, setDupCheckState] = useState(
        { checked: false, matches: [], acknowledged: false });

    const validationWarnings = useMemo(() => {
        const warnings = [];
        // 1) Duplicate items — fuzzy match on normalized name.
        const norm = (s) => (s || "").toLowerCase()
            .replace(/[^a-z0-9]+/g, " ").trim();
        const editDistance = (a, b) => {
            if (a === b) return 0;
            const dp = Array.from({ length: b.length + 1 }, (_, i) => i);
            for (let i = 1; i <= a.length; i++) {
                let prev = dp[0]; dp[0] = i;
                for (let j = 1; j <= b.length; j++) {
                    const tmp = dp[j];
                    dp[j] = a[i - 1] === b[j - 1]
                        ? prev
                        : Math.min(prev, dp[j], dp[j - 1]) + 1;
                    prev = tmp;
                }
            }
            return dp[b.length];
        };
        const sim = (a, b) => {
            const A = norm(a), B = norm(b);
            if (!A || !B) return 0;
            if (A === B) return 1;
            const d = editDistance(A, B);
            return 1 - d / Math.max(A.length, B.length);
        };
        const groups = [];
        // Only consider non-ignored items — restored duplicates count.
        const activeItems = (items || []).filter((it) => !it?.ignored);
        activeItems.forEach((it, idx) => {
            const found = groups.find(
                (g) => sim(g.rep, it?.name || it?.product_name) >= 0.85);
            if (found) found.indexes.push(idx);
            else groups.push({ rep: it?.name || it?.product_name || "",
                                indexes: [idx] });
        });
        const dupItems = groups.filter((g) => g.indexes.length > 1
                                                && g.rep.trim());
        if (dupItems.length > 0) {
            warnings.push({
                kind: "duplicate_items",
                label: t("deliveries.warn.duplicateItems", { defaultValue: "Possible duplicate items: {{list}}", list: dupItems
                    .map((g) => `"${g.rep}" ×${g.indexes.length}`).join(", ") }),
            });
        }
        // 2) Zero / negative / missing quantity or unit.
        const badQty = activeItems.filter((it) =>
            !(Number(it?.quantity) > 0)).length;
        if (badQty > 0) {
            warnings.push({
                kind: "bad_qty",
                label: t("deliveries.warn.badQty", { defaultValue: "{{count}} items have zero, negative or missing quantity", count: badQty }),
            });
        }
        const missingUnit = activeItems.filter((it) =>
            !it?.unit || !String(it.unit).trim()).length;
        if (missingUnit > 0) {
            warnings.push({
                kind: "missing_unit",
                label: t("deliveries.warn.missingUnit", { defaultValue: "{{count}} items are missing a unit", count: missingUnit }),
            });
        }
        // Prompt 45 · surface ambiguous-package lines separately so the
        // chef sees the count in the top banner and knows their stock
        // will not be written until the pack size is filled in.
        const reviewCount = activeItems.filter((it) => it?.review_required).length;
        if (reviewCount > 0) {
            warnings.push({
                kind: "review_required",
                label: t("deliveries.warn.reviewRequired", { defaultValue: "{{count}} lines need pack size — stock will not be written until confirmed", count: reviewCount }),
            });
        }
        // 3) Totals reconciliation — chef sees a warning BEFORE
        //    Confirm whenever the AI extracted invoice totals disagree
        //    with the line-level math by more than €0.05 (fixed).
        //    Never auto-corrects anything.
        //    The invoice-level `subtotal`/`vat_amount`/`total_incl_vat`
        //    (the printed Yhteensä block) INCLUDE the delivery fee, so
        //    the comparator must fold `fee.net`/`fee.vat` into the
        //    products-only line sums — matching the server-side
        //    comparator at ai-confirm time (server.py `calculated_net`).
        //    Needs-review / absent fees contribute 0.
        const T = 0.05;
        const feeNet = (deliveryFee && (deliveryFee.source === "extracted"
                        || deliveryFee.source === "manual"))
                            ? (Number(deliveryFee.net) || 0) : 0;
        const feeVat = (deliveryFee && (deliveryFee.source === "extracted"
                        || deliveryFee.source === "manual"))
                            ? (Number(deliveryFee.vat) || 0) : 0;
        const netSum = _round2(activeItems.reduce((s, it) => s + _lineNet(it), 0) + feeNet);
        const vatSum = _round2(activeItems.reduce((s, it) => s
            + _round2(_lineNet(it) * (Number(it?.vat) || 0) / 100), 0) + feeVat);
        const grossSum = _round2(netSum + vatSum);
        const invNet   = ext?.extraction?.totals?.subtotal
                            ?? ext?.totals?.subtotal
                            ?? ext?.totals?.total_amount
                            ?? ext?.total_amount;
        const invVat   = ext?.extraction?.totals?.vat_amount
                            ?? ext?.totals?.vat_amount;
        const invGross = ext?.extraction?.totals?.total_incl_vat
                            ?? ext?.totals?.total_incl_vat;
        const diffs = [];
        if (invNet   != null && Math.abs(netSum   - Number(invNet))   > T)
            diffs.push({ label: t("deliveries.exclVat", "excl. VAT"), calc: netSum,   inv: Number(invNet)   });
        if (invVat   != null && Math.abs(vatSum   - Number(invVat))   > T)
            diffs.push({ label: t("deliveries.vat", "VAT"),       calc: vatSum,   inv: Number(invVat)   });
        if (invGross != null && Math.abs(grossSum - Number(invGross)) > T)
            diffs.push({ label: t("deliveries.inclVat", "incl. VAT"), calc: grossSum, inv: Number(invGross) });
        if (diffs.length > 0) {
            const parts = diffs.map(
                (d) => t("deliveries.warn.totalPart", { defaultValue: "{{label}} calc €{{calc}} ≠ invoice €{{inv}}", label: d.label, calc: d.calc.toFixed(2), inv: d.inv.toFixed(2) }));
            warnings.push({
                kind: "total_mismatch",
                label: t("deliveries.warn.totalMismatch", { defaultValue: "Invoice totals need review — {{parts}}", parts: parts.join("; ") }),
            });
        }
        // 5) Pages not read (or only partly read) — never shown as complete.
        const unread = docPhotos.filter((p) => p.status === "error" || p.partial).length;
        if (unread > 0) {
            warnings.push({
                kind: "pages_incomplete",
                label: t("deliveries.warn.pagesIncomplete", { defaultValue: "{{count}} of {{total}} pages could not be read fully — this delivery is incomplete. Check the lines against the paper note.", count: unread, total: docPhotos.length }),
            });
        }
        // 4) Duplicate delivery match from backend.
        if (dupCheckState.matches.length > 0
                && !dupCheckState.acknowledged) {
            warnings.push({
                kind: "duplicate_delivery",
                label: dupCheckState.matches[0]?.strength === "possible"
                    ? t("deliveries.warn.duplicateDeliveryPossible", { defaultValue: "Possible duplicate: a delivery from this supplier may already exist ({{date}}) — please check", date: dupCheckState.matches[0]?.delivery_date || "?" })
                    : t("deliveries.warn.duplicateDelivery", { defaultValue: "A delivery with the same supplier and document number or file already exists ({{date}})", date: dupCheckState.matches[0]?.delivery_date || "?" }),
                matches: dupCheckState.matches,
            });
        }
        return warnings;
    }, [items, ext, dupCheckState, deliveryFee, docPhotos, t]);

    // Fire the duplicate-delivery check whenever the identifying
    // triplet is known.
    useEffect(() => {
        if (phase !== "review") return;
        if (!supplier?.id || !deliveryDate) {
            setDupCheckState({ checked: false, matches: [], acknowledged: false });
            return;
        }
        let cancelled = false;
        api.post("/deliveries/check-duplicate", {
            supplier_id: supplier.id,
            invoice_number: invoiceNumber || "",
            doc_number: docNumber || "",
            delivery_date: deliveryDate,
            total_amount: ext?.totals?.total_amount ?? ext?.total_amount ?? null,
            document_net: ext?.extraction?.totals?.subtotal ?? null,
            file_ref: ext?.file_ref || null,
            restaurant_id: restaurantId,
        }).then(({ data }) => {
            if (cancelled) return;
            setDupCheckState({
                checked: true,
                matches: data?.matches || [],
                acknowledged: false,
            });
        }).catch(() => {
            // Non-fatal — just don't show duplicate warning if the
            // pre-check itself fails.
            if (!cancelled) setDupCheckState(
                { checked: true, matches: [], acknowledged: false });
        });
        return () => { cancelled = true; };
    }, [phase, supplier?.id, invoiceNumber, docNumber, deliveryDate, ext, restaurantId]);


    useEffect(() => {
        if (!open) return;
        setPhase("attach"); setPhotos([]); setExt(null); setExtractError("");
        setSupplier({ id: null, name: "", confidence: 0, create_if_missing: false });
        setDeliveryDate(""); setDocNumber(""); setInvoiceNumber(""); setNotes("");
        setItems([]); setNewProductSeed(null);
        setAckContinueWithFailures(false);
        setGroupMode(null);
        setCurrentDocId(null);
        setPhonePending(!!autoPhone);
        phoneSeenRef.current = new Set();
        setSessionId(typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID() : `s-${Date.now()}`);
        setDupCheckState({ checked: false, matches: [], acknowledged: false });
    }, [open, autoPhone]);

    // ---------- Multi-photo capture --------------------------------------
    //
    // Photos are captured one by one (Take Photo / Upload / Scan / Connect
    // Phone) and appended to the ordered `photos[]` array. Nothing is
    // sent to the backend until the chef presses "Done" and processing
    // starts. The single-photo path (photos.length === 1) collapses to
    // exactly the same behaviour as before.
    const genPhotoId = () => (
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    );

    const addPhoto = (f, capture = null) => {
        if (!f) return;
        // Reject zero-byte / unreadable files early so we never mark a
        // ghost upload "complete" while the backend receives nothing.
        if (typeof f.size === "number" && f.size === 0) {
            toast.error(t("deliveries.imageEmptyRetake", "That image is empty or unreadable. Please retake."));
            return;
        }
        // Every page is kept; grouping is decided on the page list.
        setPhotos((prev) => [...prev, {
            id: genPhotoId(),
            file: f,
            capture,
            status: "pending",
            extraction: null,
            error: null,
        }]);
    };

    const movePhoto = (id, dir) => setPhotos((prev) => {
        const i = prev.findIndex((p) => p.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= prev.length) return prev;
        const next = [...prev];
        [next[i], next[j]] = [next[j], next[i]];
        return next;
    });

    const removePhoto = (id) => {
        setPhotos((prev) => prev.filter((p) => p.id !== id));
    };

    // Run /deliveries/ai-extract for a single captured photo.
    // Takes the photo object directly (not just its id) so we never
    // rely on the state-updater's synchronous execution — React 18
    // batching would otherwise leave the closure with a stale
    // `target` and silently bail before firing the request.
    //
    // Async architecture (2026-02-26): upload returns a `job_id` in
    // ~1 s, then we poll `GET /deliveries/ai-extract/{job_id}` every
    // 2 s until the job resolves. This survives Cloudflare's ~100 s
    // request cap even for 24-page / 200+ item PDFs. Chef only ever
    // sees "Reading document…" progress; the "job" concept is not
    // exposed in the UI copy.
    const POLL_INTERVAL_MS = 2000;
    const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 min hard cap
    const extractOnePhoto = async (photo, attempt = 1) => {
        if (!photo?.file) return;
        // Validate the file is still readable (blob URLs from camera
        // capture can be revoked before the fetch fires).
        if (typeof photo.file.size === "number" && photo.file.size === 0) {
            setPhotos((prev) => prev.map((p) => p.id === photo.id
                ? { ...p, status: "error",
                    error: t("deliveries.imageEmptyRetakeShort", "Image is empty or unreadable. Please retake.") }
                : p));
            return;
        }
        const startedSession = sessionId;
        setPhotos((prev) => prev.map((p) => p.id === photo.id
            ? { ...p, status: "reading", error: null, progress: null } : p));
        try {
            const form = new FormData();
            form.append("file", photo.file);
            // Corporate users can hold access to several restaurants —
            // the backend's `restaurant_scope` REQUIRES an explicit
            // `restaurant_id` for them (Head Chef / Restaurant Admin
            // fall back to their single assigned restaurant so passing
            // undefined is a no-op for those roles).  The same context
            // MUST accompany the follow-up poll + retry calls, else the
            // second hop would 400 with "restaurant_id is required for
            // corporate users".
            const scopeParam = (needsExplicit && restaurantId)
                ? { restaurant_id: restaurantId } : {};
            if (needsExplicit && restaurantId) form.append("restaurant_id", restaurantId);
            const { data: startResp } = await api.post(
                "/deliveries/ai-extract", form,
                { headers: { "Content-Type": "multipart/form-data" },
                  timeout: 60_000 });
            if (startedSession !== sessionId) return;
            if (!startResp?.job_id) {
                throw new Error(t("deliveries.err.uploadIncomplete", "Upload did not complete on the server. Please retry."));
            }

            // Poll until the job resolves. Backoff is a flat 2 s — the
            // work is dominated by wall-clock Gemini latency, not by
            // request overhead, so pol frequency doesn't matter much.
            const jobId = startResp.job_id;
            const deadline = Date.now() + POLL_TIMEOUT_MS;
            let data = null;
            while (Date.now() < deadline) {
                if (startedSession !== sessionId) return;
                await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
                const { data: poll } = await api.get(
                    `/deliveries/ai-extract/${jobId}`,
                    { params: scopeParam, timeout: 20_000 });
                if (startedSession !== sessionId) return;
                if (poll?.progress) {
                    setPhotos((prev) => prev.map((p) => p.id === photo.id
                        ? { ...p, progress: poll.progress } : p));
                }
                if (poll?.status === "done" || poll?.status === "partial") {
                    data = poll;
                    break;
                }
                if (poll?.status === "failed") {
                    throw new Error(translateErrorText(poll.error)
                        || t("deliveries.err.aiCouldNotReadDoc", "AI could not read this document. Try a clearer photo or a different file."));
                }
            }
            if (!data) {
                throw new Error(t("deliveries.err.readingTooLong", "Reading is taking longer than expected. Please retry."));
            }
            const hasStoredFile = !!data.file_ref;
            const hasExtraction = !!(data.extraction
                || (Array.isArray(data.matched_items) && data.matched_items.length >= 0));
            if (!hasStoredFile || !hasExtraction) {
                throw new Error(
                    t("deliveries.err.uploadIncomplete", "Upload did not complete on the server. Please retry."));
            }
            setPhotos((prev) => prev.map((p) => p.id === photo.id
                ? { ...p, status: "ok", extraction: data, error: null,
                    partial: data.status === "partial", jobId } : p));
        } catch (err) {
            // One silent auto-retry for transient network failures
            // before surfacing the error to the chef.
            if (attempt === 1) {
                await new Promise((r) => setTimeout(r, 800));
                if (startedSession === sessionId) {
                    return extractOnePhoto(photo, attempt + 1);
                }
                return;
            }
            if (startedSession !== sessionId) return;
            const msg = (err.response ? apiErrorMessage(err) : err.message)
                || t("deliveries.err.aiCouldNotReadPhoto", "AI could not read this photo");
            setPhotos((prev) => prev.map((p) => p.id === photo.id
                ? { ...p, status: "error", error: msg } : p));
        }
    };

    // "Done" — process all pending photos in capture order and, when
    // every photo has resolved (ok or the user has acknowledged a
    // continue-with-failures decision), seed the consolidated review.
    const processAllPhotos = async () => {
        if (photos.length === 0) return;
        setPhase("reading");
        setExtractError("");
        setAckContinueWithFailures(false);

        // AI disabled → go straight to blank review, but still keep
        // photos so they attach as documents on confirm.
        if (!aiEnabled) {
            setCurrentDocId(photos.find((p) => !p.saved)?.id || null);
            setExt({});
            setSupplier({ id: null, name: "", confidence: 0, create_if_missing: false });
            setDeliveryDate(new Date().toISOString().slice(0, 10));
            setDocNumber(""); setInvoiceNumber(""); setNotes(""); setItems([]);
            setPhase("review");
            return;
        }

        // Snapshot the queue up-front so mid-run photo removals don't
        // desync the loop. Sequential — one photo at a time to keep AI
        // cost and rate limits predictable and preserve capture order.
        const queue = photos.filter((p) => p.status !== "ok");
        for (const p of queue) {
            await extractOnePhoto(p);
        }
    };

    // Retry a single failed photo without touching the successes.
    // Accepts the full photo object so the fetch payload is bound at
    // call time and cannot be lost to a stale-closure race.
    const retryPhoto = async (photo) => {
        await extractOnePhoto(photo);
    };

    // User explicitly acknowledges continuing without failed photos.
    const continueWithoutFailures = () => {
        setAckContinueWithFailures(true);
    };

    // When all photos are ok (or the user ack'd to continue with only
    // the successful ones) → seed the review draft.
    useEffect(() => {
        if (phase !== "reading") return;
        if (photos.length === 0) return;
        const oks = photos.filter((p) => p.status === "ok");
        const failed = photos.filter((p) => p.status === "error");
        const pending = photos.filter((p) => p.status === "pending" || p.status === "reading");
        if (pending.length > 0) return;             // still processing
        if (failed.length > 0 && !ackContinueWithFailures) return; // wait for user
        if (oks.length === 0) return;                 // nothing to seed with
        if (groupMode === false && photos.length > 1) {
            const next = oks.find((p) => !p.saved);
            if (!next) return;
            setCurrentDocId(next.id);
            seedersRef.current.seedFromExtraction(next.extraction);
            return;
        }
        seedersRef.current.seedFromExtractions(oks.map((p) => p.extraction));
    }, [phase, photos, ackContinueWithFailures, seedersRef, groupMode]);

    // ---------- Extraction consolidation ---------------------------------
    // Merge one or more /deliveries/ai-extract responses into a single
    // review draft. Header fields prefer the first non-empty value in
    // capture order; supplier picks the highest-confidence match;
    // items are concatenated in capture order and duplicate lines
    // across overlapping photos are default-ignored with a visible
    // "possible duplicate" annotation the chef can restore.
    const seedFromExtractions = (extList) => {
        if (!extList || extList.length === 0) return;
        if (extList.length === 1) {
            // Preserves the exact single-photo seeding behaviour.
            seedFromExtraction(extList[0]);
            return;
        }
        const primary = extList[0];
        // Aggregate counts + averaged confidence for the AI summary card.
        const totals = extList.reduce((acc, e) => ({
            detected: acc.detected + (e?.counts?.detected || 0),
            matched:  acc.matched  + (e?.counts?.matched  || 0),
            new:      acc.new      + (e?.counts?.new      || 0),
        }), { detected: 0, matched: 0, new: 0 });
        const avgConfidence = extList.reduce(
            (s, e) => s + (e?.confidence || 0), 0) / extList.length;
        // Preserve primary file_ref so the existing ai-confirm flow
        // continues to move it into the delivery folder unchanged.
        // One document: printed totals / fee / VAT breakdown come from the
        // first page that prints them — never summed across pages.
        const firstPrinted = (key) => {
            const hit = extList.find((e) => e?.extraction?.[key] != null);
            return hit ? hit.extraction[key] : (primary?.extraction?.[key] ?? null);
        };
        setExt({
            ...primary,
            extraction: {
                ...(primary?.extraction || {}),
                totals: firstPrinted("totals"),
                delivery_fee: firstPrinted("delivery_fee"),
                vat_breakdown: firstPrinted("vat_breakdown"),
            },
            totals: extList.find((e) => e?.totals != null)?.totals ?? primary?.totals,
            counts: totals,
            confidence: avgConfidence,
            file_ref: primary?.file_ref,
        });

        // Supplier: highest-confidence match across photos (first wins on tie).
        const bestExt = extList.reduce((best, e) => {
            const bc = best?.supplier_match?.confidence || 0;
            const ec = e?.supplier_match?.confidence || 0;
            return ec > bc ? e : best;
        }, primary);
        const s = bestExt?.supplier_match || {};
        setSupplier({
            id: s.confidence >= 0.75 ? s.id : null,
            name: s.name || (primary?.extraction?.supplier?.name || ""),
            confidence: s.confidence || 0,
            create_if_missing: !(s.confidence >= 0.75),
        });

        // Header metadata: first non-empty in capture order.
        const firstNonEmpty = (key) => {
            for (const e of extList) {
                const v = e?.extraction?.[key];
                if (v != null && String(v).trim() !== "") return v;
            }
            return "";
        };
        setDeliveryDate(firstNonEmpty("delivery_date") || new Date().toISOString().slice(0, 10));
        setDocNumber(firstNonEmpty("doc_number") || "");
        setInvoiceNumber(firstNonEmpty("invoice_number") || "");
        setNotes("");
        const defaultVat = primary?.extraction?.vat_percent_default ?? 13.5;

        // Items: concatenate + duplicate-detect across photos.
        const normalize = (v) => String(v || "").toLowerCase().trim().replace(/\s+/g, " ");
        const dedupMap = new Map(); // key → 1-based photo index of first sighting
        const rows = [];
        let globalIdx = 0;
        extList.forEach((e, photoIdx) => {
            const photoLabel = photoIdx + 1; // 1-based, human-friendly
            (e?.matched_items || []).forEach((mi) => {
                const qty = Number(mi.extracted?.quantity || 0);
                const unit = mi.extracted?.unit || "kpl";
                const unitPrice = mi.extracted?.unit_price ?? (
                    mi.extracted?.line_price && qty
                        ? Number(mi.extracted.line_price) / qty
                        : 0);
                const matched = (mi.match?.confidence || 0) >= 0.7;
                const productKey = matched
                    ? `id:${mi.match.id}`
                    : `name:${normalize(mi.raw_name)}`;
                const dupKey = `${productKey}|${normalize(unit)}|${qty.toFixed(2)}|${Number(unitPrice).toFixed(2)}`;
                const firstSeenAt = dedupMap.get(dupKey);
                const isDuplicate = firstSeenAt !== undefined;
                if (!isDuplicate) dedupMap.set(dupKey, photoLabel);
                const conflict = !!mi.pack_conflict;
                const packCount = conflict ? (mi.extracted?.pack_count ?? null)
                    : _currentPackCount(qty, unit, mi.extracted?.pack_count,
                        mi.extracted?.pack_size, mi.extracted?.pack_unit);
                rows.push({
                    _key: `${Date.now()}-${globalIdx}`,
                    _src_idx: Number.isInteger(mi.source_index) ? mi.source_index : null,
                    _src_file_ref: e?.file_ref || null,
                    _pack_count_auto: !conflict && !(Number(mi.extracted?.pack_count) > 0) && Number(packCount) > 0,
                    _pack_conflict: conflict,
                    _source_photo: photoLabel,
                    _duplicate_of_photo: isDuplicate ? firstSeenAt : null,
                    raw_name: mi.raw_name,
                    product_id: matched ? mi.match.id : null,
                    name: matched ? mi.match.name : mi.raw_name,
                    match_confidence: mi.match?.confidence || 0,
                    match_reason: mi.match?.reason || "none",
                    quantity: mi.extracted?.quantity || 0,
                    unit,
                    price: unitPrice,
                    vat: mi.extracted?.vat_percent ?? defaultVat,
                    // Message 485 · preserve printed line NET
                    // (Kokonaishinta) alongside the chef-visible
                    // qty × unit_price. Flag flips to TRUE when the
                    // chef edits qty or price; VAT-only edits do not
                    // flip it. The server honours the preserved
                    // value for canonical VAT recompute when the
                    // row is still valid.
                    line_price: mi.extracted?.line_price ?? null,
                    line_price_invalidated: false,
                    barcode: mi.extracted?.barcode || "",
                    supplier_sku: mi.extracted?.sku || "",
                    // Prompt 45 · pack info survives the multi-photo merge.
                    pack_count: packCount,
                    pack_size: mi.extracted?.pack_size ?? null,
                    pack_unit: mi.extracted?.pack_unit || "",
                    package: mi.extracted?.package || "",
                    review_required: conflict || _isAmbiguousPackage(
                        unit,
                        packCount,
                        mi.extracted?.pack_size,
                        mi.extracted?.pack_unit),
                    new_product: !matched,
                    // Default duplicates to ignored so totals don't
                    // double-count. The chef can restore any of them
                    // in the review — they are never permanently
                    // discarded before that decision.
                    ignored: isDuplicate,
                });
                globalIdx++;
            });
        });
        setItems(rows);
        setPhase("review");
    };
    useLayoutEffect(() => {
        seedersRef.current = { seedFromExtraction, seedFromExtractions };
    });

    // ---------- Item helpers -------------------------------------------------
    const updateItem = (key, patch) => setItems((rows) =>
        rows.map((r) => {
            if (r._key !== key) return r;
            // Message 485 · qty or price edits invalidate the row's
            // printed line NET as evidence (the chef has changed the
            // row from what was printed on the document). VAT-only
            // edits do NOT invalidate NET — they only recheck VAT
            // bucket. The flag is sticky TRUE once set in this
            // Review session (keeps the subsequent qty/price moves
            // honest).
            const invalidates = Object.prototype.hasOwnProperty.call(patch, "quantity")
                || Object.prototype.hasOwnProperty.call(patch, "price");
            const nextInvalid = invalidates
                ? true
                : (r.line_price_invalidated || false);
            // An auto-derived received count follows the quantity until
            // the chef types a count of their own.
            const extra = {};
            const packEdit = ["pack_count", "pack_size", "pack_unit"].some(
                (k) => Object.prototype.hasOwnProperty.call(patch, k));
            // The chef has reviewed the changed presentation.
            if (packEdit) extra._pack_conflict = false;
            if (Object.prototype.hasOwnProperty.call(patch, "pack_count")) {
                extra._pack_count_auto = false;
            } else if (r._pack_count_auto
                    && Object.prototype.hasOwnProperty.call(patch, "quantity")
                    && Number(patch.quantity) > 0) {
                extra.pack_count = Number(patch.quantity);
            }
            return { ...r, ...patch, ...extra, line_price_invalidated: nextInvalid };
        }));
    const removeItem = (key) => setItems((rows) => rows.filter((r) => r._key !== key));

    // ---------- Final confirm ------------------------------------------------
    // Launch Fix §6 — before firing the confirmation call, ask the backend
    // whether this delivery already exists (same supplier + doc/invoice
    // number, or same supplier + date + total). The chef can either open the
    // existing record, continue anyway, or cancel.
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [skipPendingNew, setSkipPendingNew] = useState(false);

    const runDuplicateCheck = async () => {
        // No stable supplier id yet → skip (backend needs an id to match on).
        if (!supplier.id) return { has_duplicate: false };
        try {
            // Products NET — the same scope saved as `total_amount`.
            const total = _round2(items.filter((r) => !r.ignored)
                .reduce((s, r) => s + _lineNet(r), 0));
            const docNet = ext?.extraction?.totals?.subtotal;
            const { data } = await api.get("/deliveries/duplicate-check", {
                params: {
                    supplier_id: supplier.id,
                    delivery_date: deliveryDate,
                    doc_number: docNumber || "",
                    invoice_number: invoiceNumber || "",
                    total,
                    ...(docNet != null ? { document_net: docNet } : {}),
                    ...(ext?.file_ref ? { file_ref: ext.file_ref } : {}),
                    ...scopedParams,
                },
            });
            return data || { has_duplicate: false };
        } catch (_e) {
            // A failing duplicate check should never block the chef.
            return { has_duplicate: false };
        }
    };

    const confirmDelivery = async (opts = {}) => {
        const skipPendingCheck = opts.skipPendingCheck ?? skipPendingNew;
        const skipDupCheck = opts.skipDupCheck ?? false;
        if (!supplier.id && !supplier.create_if_missing) {
            toast.error(t("deliveries.err.pickSupplier", "Pick a supplier or allow creating a new one"));
            return;
        }
        if (!supplier.name?.trim()) {
            toast.error(t("deliveries.err.supplierNameRequired", "Supplier name is required"));
            return;
        }
        if (!deliveryDate) { toast.error(t("deliveries.err.dateRequired", "Delivery date is required")); return; }
        const useItems = items.filter((it) => !it.ignored);
        if (useItems.length === 0) {
            toast.error(t("deliveries.err.addOneProduct", "Add at least one product to confirm the delivery"));
            return;
        }
        // Warn about not-yet-created new products via an in-app dialog.
        const pendingNew = useItems.filter((it) => it.new_product && !it.product_id);
        if (pendingNew.length > 0 && !skipPendingCheck) {
            setPendingNewCount(pendingNew.length);
            return;
        }
        // Duplicate warning — only when the user hasn't already chosen to
        // continue past it.
        if (!skipDupCheck) {
            const res = await runDuplicateCheck();
            if (res.has_duplicate) {
                setDuplicateWarning(res);
                setSkipPendingNew(skipPendingCheck);
                return;
            }
        }
        setSaving(true);
        try {
            const body = {
                file_ref: ext?.file_ref,
                page_file_refs: docPhotos.map((p) => p.extraction?.file_ref).filter(Boolean),
                restaurant_id: needsExplicit ? restaurantId : null,
                supplier: {
                    id: supplier.id,
                    name: supplier.name.trim(),
                    create_if_missing: !supplier.id && supplier.create_if_missing,
                    // Forward the identifiers Gemini extracted so the
                    // backend can either (a) persist them on a new
                    // supplier or (b) "learn" them onto an existing
                    // one — making the NEXT delivery from this
                    // supplier deterministic (VAT ID exact-match).
                    vat_id: ext?.extraction?.supplier?.vat_id || "",
                    business_id: ext?.extraction?.supplier?.business_id || "",
                    email: ext?.extraction?.supplier?.email || "",
                },
                delivery_date: deliveryDate,
                doc_number: docNumber,
                invoice_number: invoiceNumber,
                transport_number: ext?.extraction?.transport_number || "",
                notes,
                items: items.map((it) => ({
                    raw_name: it.raw_name,
                    source_index: it._src_idx ?? null,
                    source_file_ref: it._src_file_ref || null,
                    product_id: it.product_id,
                    name: it.name,
                    quantity: Number(it.quantity) || 0,
                    unit: it.unit,
                    price: Number(it.price) || 0,
                    vat: Number(it.vat) || 0,
                    barcode: it.barcode,
                    supplier_sku: it.supplier_sku || "",
                    new_product: !!it.new_product,
                    ignored: !!it.ignored,
                    match_confidence: it.match_confidence,
                    // Prompt 45 · forward pack info so the backend can
                    // derive received_qty / received_unit for stock &
                    // last_price normalisation. review_required=true
                    // lines are persisted but skipped by the stock
                    // write until the chef re-confirms.
                    pack_count: it.pack_count == null || it.pack_count === ""
                        ? null : Number(it.pack_count),
                    pack_size: it.pack_size == null || it.pack_size === ""
                        ? null : Number(it.pack_size),
                    pack_unit: it.pack_unit || null,
                    package: it.package || "",
                    review_required: !!it.review_required,
                    // Message 485 · forward printed line NET
                    // (Kokonaishinta) + invalidation flag so the
                    // backend's canonical VAT recompute honours the
                    // printed value when the chef has not changed
                    // qty or price for this row.
                    line_price: it.line_price == null || it.line_price === ""
                        ? null : Number(it.line_price),
                    line_price_invalidated: !!it.line_price_invalidated,
                })),
                // Purchasing NET/VAT/GROSS guard — forward the AI's
                // invoice-level totals when the extractor found them.
                // Backend uses these to flag reconciliation mismatches
                // WITHOUT modifying any line price/VAT/total.
                ai_totals: (ext?.extraction?.totals
                    ? {
                        subtotal: ext.extraction.totals.subtotal ?? null,
                        vat_amount: ext.extraction.totals.vat_amount ?? null,
                        total_incl_vat: ext.extraction.totals.total_incl_vat ?? null,
                    }
                    : null),
                // Message 476 · delivery fee. Only sent when source != "none".
                delivery_fee: (deliveryFee.source && deliveryFee.source !== "none")
                    ? (() => {
                        const comps = Array.isArray(deliveryFee.components) ? deliveryFee.components : null;
                        const body_ = {
                            net: deliveryFee.net === "" ? null : Number(deliveryFee.net),
                            vat: deliveryFee.vat === "" ? null : Number(deliveryFee.vat),
                            // Message 476 Valio correction: multi-rate fees
                            // send vat_rate = null and carry the per-rate
                            // components array. Single-rate stays unchanged.
                            vat_rate: (comps && comps.length)
                                ? null
                                : (deliveryFee.vat_rate === "" ? null : Number(deliveryFee.vat_rate)),
                            source: deliveryFee.source,
                            raw_text: deliveryFee.raw_text || null,
                        };
                        if (comps && comps.length) {
                            body_.components = comps.map((c) => ({
                                net: Number(c.net), vat: Number(c.vat),
                                vat_rate: Number(c.vat_rate),
                            }));
                        }
                        return body_;
                    })()
                    : null,
            };
            const { data } = await api.post("/deliveries/ai-confirm", body);
            // Fire the standard confirm path to update inventory & set status.
            let stockSummary = null;
            try {
                const { data: confirmed } = await api.post(`/deliveries/${data.delivery_id}/confirm`);
                stockSummary = confirmed || null;
            } catch (_e) { /* keeping as draft is acceptable if confirm rules trip */ }
            // Attach ALL photos except the primary that was already
            // moved server-side by ai-confirm via `file_ref`. Failed
            // extractions still get attached — the original photo is
            // valuable evidence even when the AI could not read it.
            const primaryRef = ext?.file_ref || "";
            const extras = docPhotos.filter((p) => {
                if (!p.file) return false;
                if (!primaryRef) return true;
                return p.extraction?.file_ref !== primaryRef;
            });
            let extraFailures = 0;
            for (const p of extras) {
                try {
                    const docForm = new FormData();
                    docForm.append("file", p.file);
                    await api.post(`/deliveries/${data.delivery_id}/documents`, docForm,
                        { headers: { "Content-Type": "multipart/form-data" }});
                } catch (_e) {
                    extraFailures += 1;
                }
            }
            if (extraFailures > 0) {
                toast.warning(t("deliveries.extraPhotosFailed", { defaultValue: "{{count}} additional photos could not be attached — you can re-upload from the delivery detail.", count: extraFailures }));
            }
            // Phone pages used by this delivery are not offered again.
            await Promise.allSettled(docPhotos.filter((p) => p.capture).map((p) =>
                api.post(`/companion/captures/${p.capture.session_id}/${p.capture.capture_id}/consume`,
                    { delivery_id: data.delivery_id })));
            if (stockSummary && Number.isFinite(stockSummary.stock_updated)) {
                const parts = [t("deliveries.stockAdded", { defaultValue: "{{count}} items added to stock", count: stockSummary.stock_updated })];
                if (stockSummary.products_created > 0) {
                    parts.push(t("deliveries.newProductsCount", { defaultValue: "{{count}} new products", count: stockSummary.products_created }));
                }
                if (stockSummary.products_matched > 0) {
                    parts.push(t("deliveries.matchedCount", { defaultValue: "{{count}} matched", count: stockSummary.products_matched }));
                }
                toast.success(t("deliveries.confirmedWithParts", { defaultValue: "Delivery confirmed — {{parts}}", parts: parts.join(" · ") }));
            } else {
                toast.success(t("deliveries.confirmedInventoryUpdated", "Delivery confirmed. Inventory updated."));
            }
            if (separateMode) {
                const savedIds = new Set([...photos.filter((p) => p.saved).map((p) => p.id), currentDocId]);
                const remaining = photos.filter((p) => !savedIds.has(p.id)
                    && (aiEnabled ? p.status === "ok" : true));
                setPhotos((prev) => prev.map((p) => p.id === currentDocId ? { ...p, saved: true } : p));
                if (remaining.length > 0) {
                    toast.info(t("deliveries.nextSeparateDelivery", { defaultValue: "Delivery {{n}} of {{total}} saved — review the next one.", n: savedIds.size, total: savedIds.size + remaining.length }));
                    setDupCheckState({ checked: false, matches: [], acknowledged: false });
                    setDuplicateWarning(null); setSkipPendingNew(false);
                    if (aiEnabled) { setPhase("reading"); }
                    else {
                        setCurrentDocId(remaining[0].id);
                        setExt({}); setItems([]); setSupplier({ id: null, name: "", confidence: 0, create_if_missing: false });
                        setDocNumber(""); setInvoiceNumber(""); setNotes("");
                    }
                    return;
                }
                const notSaved = photos.length - savedIds.size;
                if (notSaved > 0) {
                    toast.warning(t("deliveries.pagesNotSaved", { defaultValue: "{{count}} pages could not be read and were not saved as deliveries.", count: notSaved }));
                }
            }
            onSavedAndOpen(data.delivery_id);
        } catch (err) {
            toast.error(apiErrorMessage(err)
                || t("deliveries.err.couldNotConfirm", "Could not confirm delivery"));
        } finally { setSaving(false); }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onClose}>
                <DialogContent
                    className="w-[95vw] max-w-5xl sm:max-w-5xl md:max-w-6xl xl:max-w-7xl"
                    data-testid="create-delivery-dialog">
                    {/* R1.1 · widened Review & Confirm dialog; presentation only. */}
                    <DialogHeader>
                        <DialogTitle>{t("deliveries.receiveDelivery", "Receive Delivery")}</DialogTitle>
                        <DialogDescription>
                            {phase === "attach" && t("deliveries.phase.attach", "Choose how to add the delivery — upload a file, connect your phone, or enter it manually.")}
                            {phase === "reading" && t("deliveries.phase.reading", "The AI is reading the delivery note…")}
                            {phase === "review" && t("deliveries.phase.review", "Review the AI-extracted delivery and confirm.")}
                            {phase === "error" && t("deliveries.phase.error", "The AI could not read the document — try again or use a different file.")}
                            {phase === "manual" && t("deliveries.phase.manual", "Enter one or more products manually. The delivery is saved as a draft — confirm it later from the delivery detail to update stock.")}
                        </DialogDescription>
                    </DialogHeader>
                    <WizardStepper phase={phase} />
                    {phase === "attach" && (
                        <>
                            <StepDeliveryNote
                                photos={photos}
                                onAddPhoto={addPhoto}
                                onRemovePhoto={removePhoto}
                                onMovePhoto={movePhoto}
                                groupMode={groupMode}
                                onGroupModeChange={setGroupMode}
                                phoneSeenRef={phoneSeenRef}
                                autoOpenPhone={phonePending}
                                onAutoPhoneOpened={() => setPhonePending(false)}
                                onDone={processAllPhotos}
                                onManual={() => setPhase("manual")}
                            />
                        </>
                    )}
                    {phase === "manual" && (
                        <ManualDeliveryStep
                            suppliers={suppliers}
                            onBack={() => setPhase("attach")}
                            onSavedAndOpen={onSavedAndOpen}
                        />
                    )}
                    {phase === "reading" && (
                        <PhotoProcessingStep
                            photos={photos}
                            onRetry={retryPhoto}
                            onRemove={removePhoto}
                            onBackToCapture={() => { setPhase("attach"); setAckContinueWithFailures(false); }}
                            onContinueWithoutFailures={continueWithoutFailures}
                            ackContinueWithFailures={ackContinueWithFailures}
                        />
                    )}
                    {phase === "error" && (
                        <AIErrorStep message={extractError}
                            onRetry={() => { setPhase("attach"); setPhotos([]); }} />
                    )}
                    {phase === "review" && (
                        <>
                            {validationWarnings.length > 0 && (
                                <div
                                    data-testid="delivery-validation-warnings"
                                    className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
                                >
                                    <div className="text-sm font-semibold text-amber-900 mb-1">
                                        ⚠ {t("deliveries.warn.header", { defaultValue: "Review required — {{count}} possible issues", count: validationWarnings.length })}
                                    </div>
                                    <ul className="text-xs text-amber-900 space-y-1 list-disc pl-5">
                                        {validationWarnings.map((w, i) => (
                                            <li key={i} data-testid={`warning-${w.kind}`}>
                                                {w.label}
                                                {w.kind === "duplicate_delivery" && (
                                                    <>
                                                        {" · "}
                                                        <a
                                                            href={`/deliveries/${w.matches[0]?.id}`}
                                                            className="underline hover:no-underline"
                                                            data-testid="view-existing-delivery"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {t("deliveries.viewExisting", "View existing")}
                                                        </a>
                                                        {" · "}
                                                        <button
                                                            type="button"
                                                            onClick={() => setDupCheckState(
                                                                (p) => ({ ...p, acknowledged: true }))}
                                                            className="underline hover:no-underline"
                                                            data-testid="acknowledge-duplicate"
                                                        >
                                                            {t("deliveries.uploadAnyway", "Upload anyway")}
                                                        </button>
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="text-[11px] text-amber-800 mt-2">
                                        {t("deliveries.warn.notBlockers", "You can still confirm — these are warnings, not blockers.")}
                                    </div>
                                </div>
                            )}
                            <AIReviewStep
                                ext={ext}
                                file={docPhotos[0]?.file || file}
                                photos={docPhotos}
                                suppliers={suppliers}
                                supplier={supplier} setSupplier={setSupplier}
                                deliveryDate={deliveryDate} setDeliveryDate={setDeliveryDate}
                                docNumber={docNumber} setDocNumber={setDocNumber}
                                invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
                                notes={notes} setNotes={setNotes}
                                deliveryFee={deliveryFee} setDeliveryFee={setDeliveryFee}
                                items={items} updateItem={updateItem} removeItem={removeItem}
                                onCreateProduct={(row) => setNewProductSeed(row)}
                            />
                        </>
                    )}
                    {phase !== "manual" && (
                        <DialogFooter className="gap-2">
                            <button data-testid="wizard-cancel" onClick={onClose}
                                className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">
                                {t("common.cancel", "Cancel")}
                            </button>
                            {phase === "review" && (
                                <button data-testid="wizard-confirm" disabled={saving}
                                    onClick={confirmDelivery}
                                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
                                    <Check className="h-4 w-4" />
                                    {saving ? t("deliveries.confirming", "Confirming…") : t("deliveries.confirmDelivery", "Confirm Delivery")}
                                </button>
                            )}
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>

            {pendingNewCount > 0 && (
                <Dialog open onOpenChange={() => setPendingNewCount(0)}>
                    <DialogContent data-testid="confirm-inline-products-dialog">
                        <DialogHeader>
                            <DialogTitle>{t("deliveries.createInlineTitle", "Create products inline?")}</DialogTitle>
                            <DialogDescription>
                                {t("deliveries.createInlineBody", { defaultValue: "{{count}} products will be created inline as receiving-only records. You can enrich them later from the Inventory page.", count: pendingNewCount })}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <button data-testid="confirm-inline-cancel"
                                onClick={() => setPendingNewCount(0)}
                                className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold">
                                {t("common.cancel", "Cancel")}
                            </button>
                            <button data-testid="confirm-inline-proceed"
                                onClick={() => { setPendingNewCount(0); confirmDelivery({ skipPendingCheck: true }); }}
                                className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
                                {t("deliveries.yesContinue", "Yes, continue")}
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {newProductSeed && (
                <NewProductFromReview
                    seed={newProductSeed}
                    onClose={() => setNewProductSeed(null)}
                    onCreated={(created) => {
                        // Attach the freshly-created product back to the row.
                        updateItem(newProductSeed._key, {
                            product_id: created.id,
                            name: created.name,
                            new_product: false,
                            match_confidence: 1.0,
                        });
                        setNewProductSeed(null);
                        toast.success(t("deliveries.productCreated", "Product created"));
                    }}
                />
            )}

            {duplicateWarning && (
                <DuplicateWarningDialog
                    warning={duplicateWarning}
                    onCancel={() => setDuplicateWarning(null)}
                    onViewExisting={(id) => {
                        setDuplicateWarning(null);
                        onSavedAndOpen(id);
                    }}
                    onContinueAnyway={() => {
                        setDuplicateWarning(null);
                        confirmDelivery({
                            skipDupCheck: true,
                            skipPendingCheck: skipPendingNew,
                        });
                    }}
                />
            )}
        </>
    );
}

// Launch Fix §6 — Delivery duplicate warning.
//
// Shown after the chef hits Confirm when the backend's duplicate-check
// endpoint reports a possible match (same supplier + doc/invoice number, or
// same supplier + date + total). The chef can open the existing record,
// continue anyway, or cancel and adjust the current delivery.
function DuplicateWarningDialog({ warning, onCancel, onViewExisting, onContinueAnyway }) {
    const { t } = useTranslation();
    const REASON_LABEL = {
        doc_number:            t("deliveries.dup.reasonDocNumber", "Same supplier and delivery number"),
        invoice_number:        t("deliveries.dup.reasonInvoiceNumber", "Same supplier and invoice number"),
        supplier_date_total:   t("deliveries.dup.reasonDateTotal", "Same supplier, date and total"),
        supplier_date:         t("deliveries.dup.reasonDate", "Same supplier and date"),
        identical_file:        t("deliveries.dup.reasonIdenticalFile", "Same supplier and identical attached file"),
        legacy_doc_number:     t("deliveries.dup.reasonLegacyDocNumber", "Possible match: same number on an older record (not verified as a delivery-note number)"),
        filename_candidate:    t("deliveries.dup.reasonFilenameCandidate", "Possible match: number taken from the file name (not verified from the document)"),
    };
    const matches = warning.matches || [];
    const reason = REASON_LABEL[warning.reason] || t("deliveries.dup.possibleMatch", "Possible match");
    return (
        <Dialog open onOpenChange={onCancel}>
            <DialogContent data-testid="duplicate-warning-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t("deliveries.dup.title", "Possible duplicate delivery detected")}</DialogTitle>
                    <DialogDescription>
                        {t("deliveries.dup.body", { defaultValue: "{{reason}}. Please confirm this is not a delivery that was already imported.", reason })}
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-72 overflow-y-auto rounded-md border border-neutral-200 divide-y divide-neutral-100"
                     data-testid="duplicate-warning-matches">
                    {matches.map((m) => (
                        <button
                            key={m.id}
                            data-testid={`duplicate-warning-match-${m.id}`}
                            onClick={() => onViewExisting(m.id)}
                            className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center justify-between gap-3"
                        >
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-neutral-900 truncate">
                                    {m.doc_number || m.invoice_number || t("deliveries.dup.noNumber", "(no delivery number)")}
                                </div>
                                <div className="text-xs text-neutral-500 mt-0.5">
                                    {m.delivery_date}
                                    {m.total_amount != null && (
                                        <> · {t("deliveries.col.total", "Total")} {Number(m.total_amount).toFixed(2)}</>
                                    )}
                                    {m.status && <> · {STATUS_LABEL[m.status] ? t(`deliveries.status.${m.status}`, STATUS_LABEL[m.status]) : m.status}</>}
                                </div>
                            </div>
                            <span className="text-xs text-neutral-500 shrink-0">{t("deliveries.view", "View")} →</span>
                        </button>
                    ))}
                    {matches.length === 0 && (
                        <div className="px-4 py-3 text-sm text-neutral-500">
                            {t("deliveries.dup.noDetails", "No details available for the matching delivery.")}
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    <button
                        data-testid="duplicate-warning-cancel"
                        onClick={onCancel}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold"
                    >
                        {t("common.cancel", "Cancel")}
                    </button>
                    <button
                        data-testid="duplicate-warning-continue"
                        onClick={onContinueAnyway}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                    >
                        {t("deliveries.continueAnyway", "Continue anyway")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function WizardStepper({ phase }) {
    const { t } = useTranslation();
    const steps = [
        { key: "attach",  label: t("deliveries.step.attach", "Attach") },
        { key: "reading", label: t("deliveries.step.reading", "AI Reading") },
        { key: "review",  label: t("deliveries.step.review", "Review & Confirm") },
    ];
    const idx = steps.findIndex((s) => s.key === (phase === "error" ? "attach" : phase));
    return (
        <div className="flex items-center gap-2 mb-4" data-testid="wizard-stepper">
            {steps.map((s, i) => {
                const state = i < idx ? "done" : i === idx ? "active" : "todo";
                return (
                    <div key={s.key} className="flex items-center gap-2 flex-1">
                        <div
                            data-testid={`wizard-step-${s.key}`}
                            data-state={state}
                            className={`h-7 w-7 grid place-items-center rounded-full text-xs font-semibold
                                ${state === "done" ? "bg-emerald-600 text-white" :
                                  state === "active" ? "bg-neutral-900 text-white" :
                                  "bg-neutral-100 text-neutral-500"}`}>
                            {state === "done" ? <Check className="h-3.5 w-3.5" /> : i + 1}
                        </div>
                        <span className={`text-xs font-medium ${state === "todo" ? "text-neutral-400" : "text-neutral-800"} hidden sm:inline`}>
                            {s.label}
                        </span>
                        {i < steps.length - 1 && (
                            <div className={`flex-1 h-px ${i < idx ? "bg-emerald-300" : "bg-neutral-200"}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function AIReadingStep({ filename }) {
    const { t } = useTranslation();
    return (
        <div className="py-10 text-center" data-testid="wizard-step-reading">
            <div className="mx-auto h-14 w-14 rounded-full bg-neutral-900 grid place-items-center animate-pulse">
                <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="mt-4 font-display text-xl font-bold">{t("deliveries.readingNote", "Reading delivery note…")}</div>
            <div className="mt-1 text-sm text-neutral-500">
                {filename ? filename : t("deliveries.analyzingDocument", "Analyzing your document")} — {t("deliveries.usuallySeconds", "usually 3–8 seconds.")}
            </div>
            <div className="mt-4 text-xs text-neutral-400">
                {t("deliveries.aiExtractingFields", "The AI is extracting supplier, date, delivery-note number and products.")}
            </div>
        </div>
    );
}

function AIErrorStep({ message, onRetry }) {
    const { t } = useTranslation();
    return (
        <div className="py-8 text-center" data-testid="wizard-step-error">
            <div className="mx-auto h-14 w-14 rounded-full bg-amber-100 grid place-items-center">
                <AlertTriangle className="h-6 w-6 text-amber-700" />
            </div>
            <div className="mt-4 font-display text-xl font-bold">{t("deliveries.couldNotReadDocument", "Could not read the document")}</div>
            <div className="mt-1 text-sm text-neutral-600 max-w-md mx-auto">{message}</div>
            <button data-testid="wizard-retry" onClick={onRetry}
                className="mt-5 h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800">
                {t("deliveries.tryAnotherDocument", "Try another document")}
            </button>
        </div>
    );
}

function AIReviewStep({ ext, file, photos = [], suppliers, supplier, setSupplier,
                        deliveryDate, setDeliveryDate,
                        docNumber, setDocNumber, invoiceNumber, setInvoiceNumber,
                        notes, setNotes,
                        deliveryFee, setDeliveryFee,
                        items, updateItem, removeItem, onCreateProduct }) {
    const { t } = useTranslation();
    const counts = ext?.counts || { detected: 0, matched: 0, new: 0 };
    const confidence = Math.round((ext?.confidence || 0) * 100);
    const total = items.filter((i) => !i.ignored)
        // Message 485 · prefer the printed `line_price` (Kokonaishinta)
        // when the chef has NOT edited qty/price for that row. Falls
        // back to qty × unit_price only when the row is invalidated.
        .reduce((sum, i) => sum + (
            i.line_price != null && !i.line_price_invalidated
                ? Number(i.line_price) || 0
                : (Number(i.quantity) || 0) * (Number(i.price) || 0)
        ), 0);
    const attachedCount = photos.length || (file ? 1 : 0);
    // Message 476 · Seed deliveryFee from the AI extraction on first
    // render. Legacy / absent fee ⇒ keep "none" (no invented charge).
    // The fee belongs to ONE document (`__doc`): a new extraction starts
    // from its own evidence (or "none") — never from a previous delivery.
    useEffect(() => {
        if (!setDeliveryFee) return;
        const seed = ext?.extraction?.delivery_fee;
        const docKey = ext?.file_ref || ext?.job_id || null;
        setDeliveryFee((cur) => {
            const isNewDoc = (cur?.__doc ?? null) !== docKey;
            if (!seed) {
                return isNewDoc
                    ? { net: "", vat: "", vat_rate: "", source: "none", raw_text: "", __doc: docKey }
                    : cur;
            }
            if (!isNewDoc && cur && cur.source && cur.source !== "none") {
                return cur;  // chef already touched it — don't clobber
            }
            return {
                net: seed.net != null ? String(seed.net) : "",
                vat: seed.vat != null ? String(seed.vat) : "",
                vat_rate: seed.vat_rate != null ? String(seed.vat_rate) : "",
                source: seed.source || "none",
                raw_text: seed.raw_text || "",
                // Message 476 Valio correction — carry per-rate
                // components when the resolver supplied them so the
                // chef sees the exact document allocation.
                components: Array.isArray(seed.components) ? seed.components : null,
                // Message 485 — immutable snapshot for "Reset from
                // document" after chef enters Manual.
                __seed_components: Array.isArray(seed.components)
                    ? seed.components.map((c) => ({ ...c })) : null,
                __doc: docKey,
            };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ext]);

    // Message 476 · Mixed-rate fee auto-resolve. When the extraction
    // resolver stamped `needs_review` (insufficient evidence at
    // extract time) AND the printed Veroerittely vat_breakdown is
    // present, recompute per-rate residuals from the CHEF-CORRECTED
    // merchandise sums. If Σ residuals reconciles to the printed fee
    // net within ±0.02, promote `source` to `manual` with the exact
    // components. Mirrors the server-side `_try_document_fee_allocation`
    // on chef-corrected data, inside the EXISTING Review fee row.
    useEffect(() => {
        if (!setDeliveryFee) return;
        const seed = ext?.extraction?.delivery_fee;
        const vb = ext?.extraction?.vat_breakdown;
        if (!seed || !Array.isArray(vb) || vb.length === 0) return;
        if ((seed.source || "") !== "needs_review") return;
        const feeNetPrinted = Number(seed.net);
        if (!Number.isFinite(feeNetPrinted) || feeNetPrinted <= 0) return;
        // Σ chef merchandise per rate (active items only — exclude only
        // ignored / 0-qty rows; `review_required` means pack-size pending
        // for inventory posting, but the qty/price/vat are valid for
        // the fee reconciliation).
        const activeItems = items.filter((it) => !it.ignored
            && Number(it.quantity) > 0 && Number(it.price) >= 0);
        const merchByRate = {};
        for (const it of activeItems) {
            const r = Number(it.vat);
            if (!Number.isFinite(r)) continue;
            // Message 485 · same printed-line-NET preference used by
            // the merchandise total badge above and by the server's
            // canonical VAT recompute.
            const t = (it.line_price != null && !it.line_price_invalidated)
                ? Number(it.line_price) || 0
                : Number(it.quantity) * Number(it.price);
            merchByRate[r] = (merchByRate[r] || 0) + t;
        }
        const comps = [];
        let sumNet = 0, sumVat = 0;
        const ALLOWED = [0, 10, 13.5, 14, 15.5, 24, 25.5];
        for (const b of vb) {
            const rate = Number(b.rate);
            const bucketNet = Number(b.net);
            if (!Number.isFinite(rate) || !Number.isFinite(bucketNet)) return;
            if (!ALLOWED.includes(rate)) return;
            const merch = merchByRate[rate] || 0;
            const residual = Math.round((bucketNet - merch) * 100) / 100;
            if (residual < -0.02) return;  // chef merch exceeds bucket → defer
            if (residual < 0.005) continue;  // this rate carries no fee
            const vatAmt = Math.round(residual * rate) / 100;
            comps.push({ net: residual, vat: vatAmt, vat_rate: rate });
            sumNet = Math.round((sumNet + residual) * 100) / 100;
            sumVat = Math.round((sumVat + vatAmt) * 100) / 100;
        }
        if (comps.length === 0) return;
        if (Math.abs(sumNet - feeNetPrinted) > 0.02) return;  // not reconciled
        setDeliveryFee((cur) => {
            // Don't clobber if chef already resolved via Manual.
            if (cur && cur.source === "manual") return cur;
            return {
                __doc: cur?.__doc ?? null,
                net: String(sumNet.toFixed(2)),
                vat: String(sumVat.toFixed(2)),
                vat_rate: "",
                source: "manual",
                raw_text: seed.raw_text || "",
                components: comps,
                // Message 485 · snapshot the document-derived
                // components so the Manual editor can offer a
                // "Reset from document" affordance.
                __seed_components: comps.map((c) => ({ ...c })),
            };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, ext]);
    return (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="wizard-step-review">
            {/* AI Summary card */}
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm"
                 data-testid="ai-summary">
                <Stat label={t("deliveries.stat.detected", "Detected")} value={counts.detected} />
                <Stat label={t("deliveries.stat.matched", "Matched")}  value={counts.matched} valueClass="text-emerald-700" />
                <Stat label={t("deliveries.stat.new", "New")}      value={counts.new} valueClass="text-amber-700" />
                <Stat label={t("deliveries.stat.confidence", "Confidence")} value={`${confidence}%`} />
                <Stat label={t("deliveries.col.total", "Total")}    value={`€ ${total.toFixed(2)}`} />
            </div>

            {/* Metadata */}
            <div className="grid gap-3 sm:grid-cols-2">
                <SupplierSelector suppliers={suppliers}
                    supplier={supplier} onChange={setSupplier} />
                <LabeledInput label={t("deliveries.deliveryDate", "Delivery Date")} type="date" value={deliveryDate}
                    onChange={setDeliveryDate} testId="review-date" />
                <LabeledInput label={t("deliveries.deliveryNoteNumber", "Delivery Note Number")} value={docNumber}
                    onChange={setDocNumber} testId="review-doc-number" />
                <LabeledInput label={t("deliveries.invoiceNumber", "Invoice Number")} value={invoiceNumber}
                    onChange={setInvoiceNumber} testId="review-invoice-number" />
            </div>

            {/* Message 476 · Delivery-fee compact review row. */}
            <DeliveryFeeReviewRow fee={deliveryFee} onChange={setDeliveryFee} ext={ext} items={items} />

            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.notes", "Notes")}</label>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="review-notes"
                    className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
            </div>

            {/* Attachment note */}
            <div className="text-xs text-neutral-600 flex items-center gap-2"
                 data-testid="review-attachment">
                <Paperclip className="h-3.5 w-3.5" />
                {attachedCount > 1 ? (
                    <>{t("deliveries.noteAttached", "Delivery note attached:")} <span className="font-semibold text-emerald-700">{t("deliveries.photosCount", { defaultValue: "{{count}} photos", count: attachedCount })}</span></>
                ) : (
                    <>{t("deliveries.noteAttached", "Delivery note attached:")} <span className="font-semibold text-emerald-700">{file?.name || t("deliveries.yes", "yes")}</span></>
                )}
            </div>

            {/* Products list */}
            <div>
                <div className="text-sm font-semibold mb-2">{t("deliveries.productsCount", { defaultValue: "Products ({{n}})", n: items.length })}</div>
                <ul className="space-y-2" data-testid="review-items-list">
                    {items.map((it) => (
                        <ReviewItemRow key={it._key} it={it}
                            onChange={(patch) => updateItem(it._key, patch)}
                            onRemove={() => removeItem(it._key)}
                            onCreateProduct={() => onCreateProduct(it)} />
                    ))}
                </ul>
            </div>
        </div>
    );
}

// Message 476 · Compact delivery-fee review row.
// Keeps the UX inside the existing Review surface — no new page, no
// Settings control. Shows source badge, net / VAT / rate, and lets the
// chef resolve a "needs_review" or edit a value before confirming.
// A fee that stays "needs_review" blocks silent VAT-0 inference on the
// server (`_validate_delivery_fee` rejects verbatim values).
function DeliveryFeeReviewRow({ fee, onChange, ext, items = [] }) {
    const { t } = useTranslation();
    const src = fee?.source || "none";
    // Message 476 Valio correction — multi-rate fee flagged when the
    // resolver supplied per-rate components. The single-rate form
    // controls are hidden; the components panel renders instead.
    const comps = Array.isArray(fee?.components) ? fee.components : null;
    const isMultiRate = !!(comps && comps.length);
    const badgeTone = (
        isMultiRate ? "bg-indigo-100 text-indigo-800" :
        src === "extracted" ? "bg-emerald-100 text-emerald-800" :
        src === "manual" ? "bg-sky-100 text-sky-800" :
        src === "explicit_zero" ? "bg-neutral-100 text-neutral-700" :
        src === "needs_review" ? "bg-amber-100 text-amber-900" :
        "bg-neutral-100 text-neutral-600");
    const badgeText = (
        isMultiRate ? t("deliveries.fee.multiRate", "Multi-rate") :
        src === "extracted" ? t("deliveries.fee.detected", "Detected") :
        src === "manual" ? t("deliveries.fee.manual", "Manual") :
        src === "explicit_zero" ? t("deliveries.fee.zero", "Zero") :
        src === "needs_review" ? t("deliveries.fee.needsReview", "Needs review") :
        t("deliveries.fee.noFee", "No fee"));
    const set = (patch) => onChange({ ...fee, ...patch });
    return (
        <div data-testid="delivery-fee-review"
             className="rounded-lg border border-neutral-200 p-3 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{t("deliveries.fee.title", "Delivery fee")}</span>
                    <span data-testid="delivery-fee-source"
                          className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${badgeTone}`}>
                        {badgeText}
                    </span>
                </div>
                <div className="flex gap-1">
                    <button type="button"
                        data-testid="delivery-fee-none"
                        onClick={() => set({ source: "none", net: "", vat: "", vat_rate: "" })}
                        className="h-7 px-2 rounded-md border border-neutral-200 text-[11px] font-semibold hover:bg-neutral-50">
                        {t("deliveries.fee.none", "None")}
                    </button>
                    <button type="button"
                        data-testid="delivery-fee-zero"
                        onClick={() => set({ source: "explicit_zero", net: "0", vat: "0", vat_rate: "0" })}
                        className="h-7 px-2 rounded-md border border-neutral-200 text-[11px] font-semibold hover:bg-neutral-50">
                        {t("deliveries.fee.zero", "Zero")}
                    </button>
                    <button type="button"
                        data-testid="delivery-fee-manual"
                        onClick={() => set({ source: "manual" })}
                        className="h-7 px-2 rounded-md border border-neutral-200 text-[11px] font-semibold hover:bg-neutral-50">
                        {t("deliveries.fee.manual", "Manual")}
                    </button>
                </div>
            </div>
            {src !== "none" && isMultiRate && (
                <div data-testid="delivery-fee-components"
                     className="space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-xs text-neutral-700 bg-neutral-50 rounded px-2 py-1.5">
                        <div data-testid="delivery-fee-total-net">
                            <span className="font-semibold">{t("deliveries.fee.netEur", "NET €")}</span> {Number(fee.net || 0).toFixed(2)}
                        </div>
                        <div data-testid="delivery-fee-total-vat">
                            <span className="font-semibold">{t("deliveries.fee.vatEur", "VAT €")}</span> {Number(fee.vat || 0).toFixed(2)}
                        </div>
                        <div data-testid="delivery-fee-total-gross">
                            <span className="font-semibold">{t("deliveries.fee.grossEur", "GROSS €")}</span> {(Number(fee.net || 0) + Number(fee.vat || 0)).toFixed(2)}
                        </div>
                    </div>
                    <div className="rounded border border-neutral-200 overflow-hidden">
                        <div className="grid grid-cols-5 bg-neutral-100 text-[9px] sm:text-[10px] uppercase font-semibold text-neutral-600 px-2 py-1 gap-1 [&>div]:min-w-0 [&>div]:truncate">
                            <div>{t("deliveries.fee.rate", "Rate")}</div><div className="text-right">{t("deliveries.fee.netEur", "NET €")}</div>
                            <div className="text-right">{t("deliveries.fee.vatEur", "VAT €")}</div><div className="text-right">{t("deliveries.fee.grossEur", "GROSS €")}</div>
                            <div></div>
                        </div>
                        {comps.map((c, i) => {
                            const editable = src === "manual";
                            const onComp = (patch) => {
                                const next = comps.map((cc, j) => j === i ? { ...cc, ...patch } : cc);
                                // HALF_UP VAT recompute per row, then sum
                                // the whole array into top-level net/vat.
                                const normalised = next.map((cc) => {
                                    const n = Number(cc.net) || 0;
                                    const r = Number(cc.vat_rate) || 0;
                                    const v = Math.round(n * r) / 100;
                                    return { net: n, vat: v, vat_rate: r };
                                });
                                const sumNet = normalised.reduce((s, cc) =>
                                    Math.round((s + cc.net) * 100) / 100, 0);
                                const sumVat = normalised.reduce((s, cc) =>
                                    Math.round((s + cc.vat) * 100) / 100, 0);
                                set({ components: normalised,
                                       net: String(sumNet.toFixed(2)),
                                       vat: String(sumVat.toFixed(2)),
                                       vat_rate: null });
                            };
                            const onRemoveComp = () => {
                                const next = comps.filter((_, j) => j !== i);
                                if (next.length === 0) {
                                    set({ components: null,
                                          net: "", vat: "", vat_rate: "" });
                                    return;
                                }
                                const sumNet = next.reduce((s, cc) =>
                                    Math.round((s + (Number(cc.net) || 0)) * 100) / 100, 0);
                                const sumVat = next.reduce((s, cc) =>
                                    Math.round((s + (Number(cc.vat) || 0)) * 100) / 100, 0);
                                set({ components: next,
                                       net: String(sumNet.toFixed(2)),
                                       vat: String(sumVat.toFixed(2)),
                                       vat_rate: null });
                            };
                            return (
                                <div key={i}
                                     data-testid={`delivery-fee-component-${i}`}
                                     className="grid grid-cols-5 px-2 py-1 text-xs border-t border-neutral-100 gap-1 items-center">
                                    {editable ? (
                                        <select
                                            data-testid={`delivery-fee-component-${i}-rate`}
                                            value={String(c.vat_rate)}
                                            onChange={(e) => onComp({ vat_rate: Number(e.target.value) })}
                                            className="h-8 px-1 border border-neutral-300 rounded text-xs">
                                            {[0, 10, 13.5, 14, 15.5, 24, 25.5].map((rr) => (
                                                <option key={rr} value={rr}>{rr.toFixed(1)} %</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div data-testid={`delivery-fee-component-${i}-rate`}>{Number(c.vat_rate).toFixed(1)} %</div>
                                    )}
                                    {editable ? (
                                        <input type="number" step="0.01" min="0"
                                            data-testid={`delivery-fee-component-${i}-net`}
                                            value={c.net ?? ""}
                                            onChange={(e) => onComp({ net: e.target.value === "" ? 0 : Number(e.target.value) })}
                                            className="h-8 px-1 border border-neutral-300 rounded text-xs text-right" />
                                    ) : (
                                        <div className="text-right" data-testid={`delivery-fee-component-${i}-net`}>{Number(c.net).toFixed(2)}</div>
                                    )}
                                    <div className="text-right" data-testid={`delivery-fee-component-${i}-vat`}>{Number(c.vat).toFixed(2)}</div>
                                    <div className="text-right">{(Number(c.net) + Number(c.vat)).toFixed(2)}</div>
                                    {editable ? (
                                        <button type="button"
                                            data-testid={`delivery-fee-component-${i}-remove`}
                                            onClick={onRemoveComp}
                                            className="h-7 w-7 justify-self-end rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 grid place-items-center">×</button>
                                    ) : (<div></div>)}
                                </div>
                            );
                        })}
                        {src === "manual" && (
                            <div className="flex items-center justify-between px-2 py-1 border-t border-dashed border-neutral-200 bg-neutral-50">
                                <button type="button"
                                    data-testid="delivery-fee-component-add"
                                    onClick={() => {
                                        const next = [...(comps || []),
                                            { net: 0, vat: 0, vat_rate: 13.5 }];
                                        set({ components: next, vat_rate: null });
                                    }}
                                    className="text-[11px] font-semibold text-emerald-700 hover:underline">
                                    + {t("deliveries.fee.addRate", "Add rate")}
                                </button>
                                <button type="button"
                                    data-testid="delivery-fee-reset-from-document"
                                    onClick={() => {
                                    const seed = fee.__seed_components || null;
                                    if (!seed || !seed.length) return;
                                    // Message 485 — Reset re-validates
                                    // against CURRENT merchandise rows.
                                    // If quantities/prices/VAT changed
                                    // so the printed VAT buckets no
                                    // longer reconcile with the fee
                                    // allocation (±€0,02 per bucket),
                                    // we drop back to needs_review
                                    // instead of restoring stale
                                    // components.
                                    const vb = ext?.extraction?.vat_breakdown;
                                    const feeAmt = Number(fee.net || 15);
                                    let valid = true;
                                    if (Array.isArray(vb) && Array.isArray(items)) {
                                        const merchByRate = {};
                                        items.filter((x) => !x.ignored).forEach((x) => {
                                            const r = Number(x.vat) || 0;
                                            const base = (x.line_price != null && !x.line_price_invalidated)
                                                ? Number(x.line_price)
                                                : (Number(x.quantity) || 0) * (Number(x.price) || 0);
                                            merchByRate[r] = (merchByRate[r] || 0) + base;
                                        });
                                        const residuals = {};
                                        vb.forEach((b) => {
                                            const r = Number(b.rate);
                                            residuals[r] = Math.round((Number(b.net) - (merchByRate[r] || 0)) * 100) / 100;
                                        });
                                        const sumResid = Object.values(residuals)
                                            .reduce((s, v) => Math.round((s + v) * 100) / 100, 0);
                                        if (Math.abs(sumResid - feeAmt) > 0.02) valid = false;
                                        // Also validate that seed component rates match current residuals.
                                        seed.forEach((c) => {
                                            if (!(c.vat_rate in residuals)) valid = false;
                                            else if (Math.abs(residuals[c.vat_rate] - Number(c.net)) > 0.02) valid = false;
                                        });
                                    }
                                    if (valid) {
                                        const sumNet = seed.reduce((s, cc) =>
                                            Math.round((s + Number(cc.net)) * 100) / 100, 0);
                                        const sumVat = seed.reduce((s, cc) =>
                                            Math.round((s + Number(cc.vat)) * 100) / 100, 0);
                                        set({ components: seed.map((cc) => ({ ...cc })),
                                               net: String(sumNet.toFixed(2)),
                                               vat: String(sumVat.toFixed(2)),
                                               vat_rate: null,
                                               source: "extracted" });
                                    } else {
                                        // Evidence no longer reconciles — drop to needs_review.
                                        set({ components: null,
                                               net: String(feeAmt.toFixed(2)),
                                               vat: "", vat_rate: null,
                                               source: "needs_review" });
                                    }
                                }}
                                    className="text-[11px] text-neutral-500 hover:underline">
                                    {t("deliveries.fee.resetFromDocument", "Reset from document")}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="text-[11px] text-neutral-500">
                        {src === "manual"
                            ? <>{t("deliveries.fee.manualMixedHint", "Manual mixed-rate. Edit per-rate rows above; totals and VAT update automatically.")}</>
                            : <>{t("deliveries.fee.allocatedHint", "Allocated from the document's printed VAT breakdown. Edit as single-rate instead by pressing")} <span className="font-semibold">{t("deliveries.fee.manual", "Manual")}</span>.</>}
                    </div>
                </div>
            )}
            {src !== "none" && !isMultiRate && (
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-600">{t("deliveries.fee.netEur", "NET €")}</label>
                        <input type="number" step="0.01" min="0"
                            data-testid="delivery-fee-net"
                            value={fee.net || ""}
                            onChange={(e) => {
                                const n = e.target.value;
                                const rate = Number(fee.vat_rate || 0);
                                // MiseOne canonical HALF_UP policy (money.py).
                                // JS .toFixed(2) is unreliable for .x25 cases.
                                const auto = (n !== "" && !isNaN(rate))
                                    ? (Math.round(Number(n) * rate) / 100).toFixed(2) : fee.vat;
                                set({ net: n, vat: auto, source: src === "needs_review" ? "needs_review" : (src === "none" ? "manual" : src) });
                            }}
                            disabled={src === "explicit_zero"}
                            className="mt-1 w-full h-10 px-2 border border-neutral-300 rounded text-sm" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-600">{t("deliveries.fee.vatPct", "VAT %")}</label>
                        <select data-testid="delivery-fee-vat-rate"
                            value={fee.vat_rate || ""}
                            onChange={(e) => {
                                const r = e.target.value;
                                // MiseOne canonical HALF_UP policy.
                                const auto = (fee.net !== "" && r !== "")
                                    ? (Math.round(Number(fee.net) * Number(r)) / 100).toFixed(2) : fee.vat;
                                set({ vat_rate: r, vat: auto, source: src === "needs_review" ? "needs_review" : src });
                            }}
                            disabled={src === "explicit_zero"}
                            className="mt-1 w-full h-10 px-2 border border-neutral-300 rounded text-sm">
                            <option value="">—</option>
                            <option value="0">0 %</option>
                            <option value="10">10 %</option>
                            <option value="13.5">13.5 %</option>
                            <option value="14">14 %</option>
                            <option value="15.5">15.5 %</option>
                            <option value="24">24 %</option>
                            <option value="25.5">25.5 %</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-600">{t("deliveries.fee.vatEur", "VAT €")}</label>
                        <input type="number" step="0.01" min="0"
                            data-testid="delivery-fee-vat"
                            value={fee.vat || ""}
                            onChange={(e) => set({ vat: e.target.value })}
                            disabled={src === "explicit_zero"}
                            className="mt-1 w-full h-10 px-2 border border-neutral-300 rounded text-sm" />
                    </div>
                </div>
            )}
            {src === "needs_review" && (
                <div data-testid="delivery-fee-needs-review"
                     className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    {t("deliveries.fee.needsReviewHint", "This transport line was detected but tax/amount is unclear. Resolve NET + VAT %, or press Zero / None, before confirming.")}
                </div>
            )}
            {fee.raw_text && (
                <div className="mt-2 text-[11px] text-neutral-500 truncate"
                     data-testid="delivery-fee-raw-text">
                    {t("deliveries.fee.fromDocument", "From document:")} "{fee.raw_text}"
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, valueClass = "" }) {
    return (
        <div className="text-center">
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">{label}</div>
            <div className={`text-lg font-bold ${valueClass}`}>{value}</div>
        </div>
    );
}

function LabeledInput({ label, value, onChange, type = "text", testId }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{label}</label>
            <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md text-sm" />
        </div>
    );
}

function SupplierSelector({ suppliers, supplier, onChange }) {
    const { t } = useTranslation();
    // If we already have an id, show a Select; if not, offer the create-toggle.
    const highConf = supplier.confidence >= 0.75 && supplier.id;
    return (
        <div data-testid="review-supplier">
            <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("deliveries.col.supplier", "Supplier")}</label>
            {supplier.id ? (
                <Select value={supplier.id}
                    onValueChange={(v) => onChange({ ...supplier, id: v, name: suppliers.find((s) => s.id === v)?.name || "" })}>
                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : (
                <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <div className="text-sm">
                        <span className="font-semibold">{t("deliveries.supplierNotRecognized", "Supplier not recognized.")}</span> {t("deliveries.createSupplierQ", "Create")}{" "}
                        <span className="font-semibold">{supplier.name || t("deliveries.unknown", "(unknown)")}</span>?
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <input value={supplier.name || ""}
                            onChange={(e) => onChange({ ...supplier, name: e.target.value })}
                            data-testid="review-supplier-name"
                            placeholder={t("deliveries.supplierName", "Supplier name")}
                            className="flex-1 h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                        <button data-testid="review-supplier-create"
                            onClick={() => onChange({ ...supplier, create_if_missing: true })}
                            className={`h-10 px-3 rounded-md text-xs font-semibold ${supplier.create_if_missing ? "bg-emerald-600 text-white" : "border border-neutral-300 hover:bg-white"}`}>
                            {supplier.create_if_missing ? t("deliveries.yesCreate", "Yes, create") : t("deliveries.yesBtn", "Yes")}
                        </button>
                        <Select onValueChange={(v) => onChange({ ...supplier, id: v, name: suppliers.find((s) => s.id === v)?.name || "", create_if_missing: false })}>
                            <SelectTrigger className="h-10 w-40" data-testid="review-supplier-existing">
                                <SelectValue placeholder={t("deliveries.pickExisting", "Pick existing")} />
                            </SelectTrigger>
                            <SelectContent>
                                {suppliers.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}
            {highConf && (
                <div className="mt-1 text-[11px] text-emerald-700">
                    {t("deliveries.matchedAutomatically", { defaultValue: "Matched automatically ({{pct}}% confidence).", pct: Math.round(supplier.confidence * 100) })}
                </div>
            )}
        </div>
    );
}

function ReviewItemRow({ it, onChange, onRemove, onCreateProduct }) {
    const { t } = useTranslation();
    const matched = it.match_confidence >= 0.7 && !it.new_product;
    const isDuplicate = !!it._duplicate_of_photo;
    // Prompt 45 · Auto-recompute review_required whenever pack/unit
    // change so the badge stays honest without a manual toggle.
    // A presentation that differs from the remembered package also
    // needs review until the chef confirms a pack field.
    const ambiguous = !!it._pack_conflict || _isAmbiguousPackage(
        it.unit, it.pack_count, it.pack_size, it.pack_unit);
    const received = _derivedReceived(it);
    const normCost = _normalizedUnitCost(it);
    const hasAnyPack = it.pack_count || it.pack_size || it.pack_unit
                            || it.package;
    return (
        <li className={`rounded-md border p-3 grid grid-cols-12 gap-2 items-center
                        ${it.ignored ? "opacity-40 bg-neutral-50" :
                          ambiguous ? "border-rose-300 bg-rose-50/60" :
                          matched ? "border-emerald-200 bg-emerald-50/40" :
                          "border-amber-200 bg-amber-50/40"}`}
            data-testid={`review-item-${it._key}`}>
            <div className="col-span-12 sm:col-span-4">
                <div className="font-medium text-sm truncate" title={it.raw_name}>{it.raw_name}</div>
                <div className="text-[11px] text-neutral-500 truncate flex items-center gap-1 flex-wrap">
                    {matched
                        ? <>{t("deliveries.stat.matched", "Matched")} · <span className="text-emerald-700 font-semibold">{it.name}</span> ({Math.round(it.match_confidence * 100)}%)</>
                        : <span className="text-amber-700 font-semibold">{t("deliveries.newProduct", "New product")}</span>}
                    {it._source_photo != null && (
                        <span className="inline-block px-1.5 py-0.5 rounded-full bg-neutral-200 text-neutral-700 text-[10px] font-semibold">
                            {t("deliveries.photoN", { defaultValue: "Photo {{n}}", n: it._source_photo })}
                        </span>
                    )}
                    {ambiguous && (
                        <span
                            data-testid={`review-item-review-required-${it._key}`}
                            className="inline-block px-1.5 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-semibold"
                            title={t("deliveries.ambiguousPackageTip", "Ambiguous package — enter pack size before confirming so stock is written in the correct physical unit.")}
                        >
                            {t("deliveries.reviewRequired", "Review required")}
                        </span>
                    )}
                    {isDuplicate && (
                        <span
                            data-testid={`review-item-duplicate-${it._key}`}
                            className="inline-block px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-semibold"
                            title={t("deliveries.possibleDuplicateTip", { defaultValue: "Possible duplicate of a line from Photo {{n}}. Restore to include it in the delivery.", n: it._duplicate_of_photo })}
                        >
                            {t("deliveries.possibleDuplicatePhoto", { defaultValue: "Possible duplicate — Photo {{n}}", n: it._duplicate_of_photo })}
                        </span>
                    )}
                </div>
            </div>
            <input type="number" step="0.01" value={it.quantity}
                onChange={(e) => onChange({ quantity: e.target.value })}
                aria-label={t("deliveries.qty", "Quantity")}
                data-testid={`review-item-qty-${it._key}`}
                className="col-span-3 sm:col-span-1 h-9 px-2 border border-neutral-300 rounded-md text-sm" />
            <input value={it.unit}
                onChange={(e) => {
                    const v = e.target.value;
                    onChange({
                        unit: v,
                        review_required: _isAmbiguousPackage(
                            v, it.pack_count, it.pack_size, it.pack_unit),
                    });
                }}
                aria-label={t("deliveries.unit", "Unit")}
                data-testid={`review-item-unit-${it._key}`}
                className="col-span-3 sm:col-span-1 h-9 px-2 border border-neutral-300 rounded-md text-sm" />
            <input type="number" step="0.01" value={it.price}
                onChange={(e) => onChange({ price: e.target.value })}
                aria-label={t("deliveries.unitPrice", "Unit price")}
                data-testid={`review-item-price-${it._key}`}
                className="col-span-3 sm:col-span-2 h-9 px-2 border border-neutral-300 rounded-md text-sm" />
            <input type="number" step="0.5" value={it.vat}
                onChange={(e) => onChange({ vat: e.target.value })}
                aria-label={t("deliveries.vatPct", "VAT %")}
                data-testid={`review-item-vat-${it._key}`}
                className="col-span-3 sm:col-span-1 h-9 px-2 border border-neutral-300 rounded-md text-sm" />
            {/* R1.1 · per-line VAT breakdown (display-only, no math on write path). */}
            <div
                className="col-span-12 sm:col-span-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] tabular-nums"
                data-testid={`review-item-pricing-${it._key}`}>
                <span>
                    <span className="text-neutral-500">{t("deliveries.exclVatCap", "Excl. VAT")} </span>
                    <span
                        className="font-semibold text-neutral-800"
                        data-testid={`review-item-price-excl-${it._key}`}>
                        € {(Number(it.price) || 0).toFixed(2)}
                    </span>
                </span>
                <span>
                    <span className="text-neutral-500">{t("deliveries.vat", "VAT")} </span>
                    <span
                        className="font-semibold text-neutral-800"
                        data-testid={`review-item-price-vat-pct-${it._key}`}>
                        {(Number(it.vat) || 0).toFixed(1)}%
                    </span>
                </span>
                <span>
                    <span className="text-neutral-500">{t("deliveries.inclVatCap", "Incl. VAT")} </span>
                    <span
                        className="font-semibold text-neutral-800"
                        data-testid={`review-item-price-incl-${it._key}`}>
                        € {((Number(it.price) || 0) * (1 + (Number(it.vat) || 0) / 100)).toFixed(2)}
                    </span>
                </span>
            </div>
            <div className="col-span-12 sm:col-span-3 flex items-center gap-1 justify-end">
                {!matched && !it.ignored && (
                    <button data-testid={`review-item-create-${it._key}`}
                        onClick={onCreateProduct}
                        className="h-9 px-2 rounded-md border border-neutral-300 text-xs font-semibold hover:bg-white">
                        {t("deliveries.createProduct", "Create Product")}
                    </button>
                )}
                <button data-testid={`review-item-toggle-ignore-${it._key}`}
                    onClick={() => onChange({ ignored: !it.ignored })}
                    className={`h-9 px-2 rounded-md text-xs font-semibold ${it.ignored ? "bg-neutral-900 text-white" : "border border-neutral-300 hover:bg-white"}`}>
                    {it.ignored ? t("deliveries.restore", "Restore") : t("deliveries.ignore", "Ignore")}
                </button>
                <button data-testid={`review-item-remove-${it._key}`}
                    onClick={onRemove}
                    aria-label={t("deliveries.removeLine", "Remove line")}
                    className="h-9 w-9 grid place-items-center rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>
            {/* Prompt 45 · Pack sub-row — visible only when the line
                has any pack information OR is flagged ambiguous. Direct
                lines (e.g. "10 kg carrot") stay compact per spec §E. */}
            {(hasAnyPack || ambiguous) && (
                <div className="col-span-12 mt-2 pt-2 border-t border-dashed border-neutral-200 grid grid-cols-12 gap-2 items-center"
                     data-testid={`review-item-pack-row-${it._key}`}>
                    <div className="col-span-12 sm:col-span-4 text-[11px] text-neutral-500">
                        {t("deliveries.package", "Package")}
                    </div>
                    <input type="number" step="1" min="0"
                        value={it.pack_count ?? ""}
                        placeholder={t("deliveries.packCount", "count")}
                        onChange={(e) => {
                            const v = e.target.value === "" ? null : Number(e.target.value);
                            onChange({
                                pack_count: v,
                                review_required: _isAmbiguousPackage(
                                    it.unit, v, it.pack_size, it.pack_unit),
                            });
                        }}
                        data-testid={`review-item-pack-count-${it._key}`}
                        className="col-span-3 sm:col-span-1 h-9 px-2 border border-neutral-300 rounded-md text-sm bg-white" />
                    <div className="col-span-1 text-center text-neutral-400 text-sm">×</div>
                    <input type="number" step="0.001" min="0"
                        value={it.pack_size ?? ""}
                        placeholder={t("deliveries.packSize", "size")}
                        onChange={(e) => {
                            const v = e.target.value === "" ? null : Number(e.target.value);
                            onChange({
                                pack_size: v,
                                review_required: _isAmbiguousPackage(
                                    it.unit, it.pack_count, v, it.pack_unit),
                            });
                        }}
                        data-testid={`review-item-pack-size-${it._key}`}
                        className="col-span-3 sm:col-span-1 h-9 px-2 border border-neutral-300 rounded-md text-sm bg-white" />
                    <input value={it.pack_unit ?? ""}
                        placeholder={t("deliveries.packUnit", "unit")}
                        onChange={(e) => {
                            const v = e.target.value;
                            onChange({
                                pack_unit: v,
                                review_required: _isAmbiguousPackage(
                                    it.unit, it.pack_count, it.pack_size, v),
                            });
                        }}
                        data-testid={`review-item-pack-unit-${it._key}`}
                        className="col-span-3 sm:col-span-1 h-9 px-2 border border-neutral-300 rounded-md text-sm bg-white" />
                    <div className="col-span-12 sm:col-span-4 text-[11px] text-right text-neutral-600"
                         data-testid={`review-item-derived-${it._key}`}>
                        {received ? (
                            <>
                                {t("deliveries.received", "Received:")} <b>{Number(received.qty).toFixed(3)
                                    .replace(/\.?0+$/, "")}&nbsp;{received.unit}</b>
                                {normCost && (
                                    <span className="ml-2">
                                        · €{Number(normCost.value).toFixed(2)}/{normCost.unit}
                                    </span>
                                )}
                            </>
                        ) : ambiguous ? (
                            <span className="text-rose-700 font-semibold">
                                {t("deliveries.enterPackSize", "Enter pack size to derive received qty")}
                            </span>
                        ) : (
                            <span className="text-neutral-400">
                                {t("deliveries.direct", "Direct:")} {Number(it.quantity) || 0} {it.unit}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </li>
    );
}

function NewProductFromReview({ seed, onClose, onCreated }) {
    const { t } = useTranslation();
    // Minimal in-place "New Product" dialog seeded from the review row.
    // Kept intentionally lightweight — restaurant chef fills in the essentials
    // and the freshly-created product is linked back to the delivery row.
    const { withRestaurant } = useRestaurantScope();
    const [name, setName]     = useState(seed?.raw_name || "");
    const [unit, setUnit]     = useState(seed?.unit || "kpl");
    const [barcode, setBarcode] = useState(seed?.barcode || "");
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!name.trim()) { toast.error(t("deliveries.err.nameRequired", "Name required")); return; }
        setSaving(true);
        try {
            const body = withRestaurant({ name: name.trim(), unit, barcode });
            const { data } = await api.post("/products", body);
            onCreated({ id: data.id, name: name.trim() });
        } catch (e) {
            toast.error(apiErrorMessage(e) || t("deliveries.err.couldNotCreateProduct", "Could not create product"));
        } finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="new-product-from-review-dialog">
                <DialogHeader>
                    <DialogTitle>{t("deliveries.createProductTitle", "Create product")}</DialogTitle>
                    <DialogDescription>
                        {t("deliveries.prefilledFromAi", "Pre-filled from the AI extraction — review the essentials.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <LabeledInput label={t("common.name", "Name")} value={name} onChange={setName} testId="npr-name" />
                    <LabeledInput label={t("deliveries.unit", "Unit")} value={unit} onChange={setUnit} testId="npr-unit" />
                    <LabeledInput label={t("deliveries.barcode", "Barcode")} value={barcode} onChange={setBarcode} testId="npr-barcode" />
                </div>
                <DialogFooter>
                    <button onClick={onClose}
                        className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">
                        {t("common.cancel", "Cancel")}
                    </button>
                    <button data-testid="npr-save" onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("common.saving", "Saving…") : t("deliveries.createProductTitle", "Create product")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ───────────────────────────────────────────────────────────────────
// Manual Delivery entry — one or multiple product rows, no attachment.
//
// Save creates a NORMAL unconfirmed delivery through the canonical
// endpoints (/deliveries + /deliveries/{id}/items). It never calls
// /confirm — the chef must open the delivery detail and press
// Confirm Delivery to post stock. This preserves the single
// inventory-write path shared with every other delivery source.
// ───────────────────────────────────────────────────────────────────
function ManualDeliveryStep({ suppliers, onBack, onSavedAndOpen }) {
    const { t } = useTranslation();
    const { restaurantId } = useApp();
    const { needsExplicit, requireSelection, withRestaurant } = useRestaurantScope();

    const [supplier, setSupplier] = useState(
        { id: null, name: "", confidence: 0, create_if_missing: false });
    const [deliveryDate, setDeliveryDate] = useState(
        () => new Date().toISOString().slice(0, 10));
    const [docNumber, setDocNumber] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [notes, setNotes] = useState("");
    const [items, setItems] = useState(() => [newManualRow()]);
    const [saving, setSaving] = useState(false);

    // Catalogue of existing products for the inline picker. Loaded
    // once per open; the typed name is matched case-insensitively.
    const [catalogue, setCatalogue] = useState([]);
    useEffect(() => {
        if (needsExplicit && !restaurantId) return;
        const params = needsExplicit ? { restaurant_id: restaurantId, limit: 500 } : { limit: 500 };
        api.get("/products", { params }).then(({ data }) => {
            setCatalogue(Array.isArray(data?.items) ? data.items : []);
        }).catch(() => { /* non-fatal — rows default to new products */ });
    }, [restaurantId, needsExplicit]);

    // Merged built-in + custom product categories, so each row can
    // assign a category when creating a new product inline.
    const [categories, setCategories] = useState([]);
    const loadCategories = async () => {
        try {
            const { data } = await api.get("/categories", {
                params: { type: "product", include_builtin: 1 },
            });
            setCategories(Array.isArray(data) ? data : []);
        } catch (_e) { /* non-fatal: category dropdown falls back to names */ }
    };
    useEffect(() => { loadCategories(); }, []);

    // Inline "+ Add category" dialog (mirrors Inventory EditProductDialog).
    const [addCatOpen, setAddCatOpen] = useState(false);
    const [newCatName, setNewCatName] = useState("");
    const [newCatForRow, setNewCatForRow] = useState(null);
    const [creatingCat, setCreatingCat] = useState(false);
    const submitNewCategory = async () => {
        const nm = newCatName.trim();
        if (!nm) { toast.error(t("deliveries.err.categoryNameRequired", "Category name is required")); return; }
        setCreatingCat(true);
        try {
            await api.post("/categories", { type: "product", name: nm });
            toast.success(t("deliveries.categoryCreated", { defaultValue: "Category \"{{name}}\" created", name: nm }));
            await loadCategories();
            if (newCatForRow) {
                updateRow(newCatForRow, { category: nm });
            }
            setAddCatOpen(false);
            setNewCatName("");
            setNewCatForRow(null);
        } catch (e) {
            toast.error(apiErrorMessage(e)
                || t("deliveries.err.couldNotCreateCategory", "Could not create category"));
        } finally { setCreatingCat(false); }
    };

    const updateRow = (key, patch) => setItems((rows) =>
        rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
    const removeRow = (key) => setItems((rows) =>
        rows.length <= 1 ? rows : rows.filter((r) => r._key !== key));
    const addRow = () => setItems((rows) => [...rows, newManualRow()]);

    // Per-row NET/VAT/GROSS preview (presentation only — the backend
    // keeps the single source of truth via per-line HALF_UP rounding
    // in the canonical confirm path).
    const totals = useMemo(() => {
        let net = 0, vat = 0;
        for (const r of items) {
            const q = Number(r.quantity) || 0;
            const p = Number(r.price) || 0;
            const v = Number(r.vat) || 0;
            const line = q * p;
            net += line;
            vat += line * v / 100;
        }
        return { net, vat, gross: net + vat };
    }, [items]);

    const save = async () => {
        if (needsExplicit && !requireSelection()) return;
        if (!supplier.id && !supplier.create_if_missing) {
            toast.error(t("deliveries.err.pickSupplier", "Pick a supplier or allow creating a new one"));
            return;
        }
        if (!supplier.name?.trim() && !supplier.id) {
            toast.error(t("deliveries.err.supplierNameRequired", "Supplier name is required"));
            return;
        }
        if (!deliveryDate) { toast.error(t("deliveries.err.dateRequired", "Delivery date is required")); return; }
        const useItems = items.filter((r) => (r.name || "").trim());
        if (useItems.length === 0) {
            toast.error(t("deliveries.err.addOneRow", "Add at least one product row"));
            return;
        }
        for (const r of useItems) {
            if (!(Number(r.quantity) > 0)) {
                toast.error(t("deliveries.err.qtyRequiredFor", { defaultValue: "Quantity required for \"{{name}}\"", name: r.name }));
                return;
            }
            if (!(r.unit || "").trim()) {
                toast.error(t("deliveries.err.unitRequiredFor", { defaultValue: "Unit required for \"{{name}}\"", name: r.name }));
                return;
            }
        }
        setSaving(true);
        try {
            // 1. Supplier — reuse existing POST /suppliers for create path.
            let supplierId = supplier.id;
            if (!supplierId) {
                const body = withRestaurant({ name: supplier.name.trim() });
                const { data } = await api.post("/suppliers", body);
                supplierId = data.id;
            }
            // 2. Create products inline for rows that have no product_id.
            //    Reuses POST /products — identical to the AI flow's
            //    "Create Product" sub-dialog and the Inventory → New.
            const resolved = [];
            for (const r of useItems) {
                let pid = r.product_id || null;
                if (!pid) {
                    const explicitCategory = (r.category || "").trim();
                    const prodBody = withRestaurant({
                        product_name: r.name.trim(),
                        unit: r.unit || "kpl",
                        vat: Number(r.vat) || 14,
                        barcode: r.barcode || "",
                        last_price: Number(r.price) || 0,
                        supplier_id: supplierId,
                        // When the chef picks a category (built-in OR custom
                        // such as "Cafés") we store it verbatim and NEVER
                        // ask the backend to classify. When the chef makes
                        // NO selection we omit `category` and set the
                        // opt-in `auto_classify` flag, which tells
                        // `POST /products` to run the deterministic
                        // keyword classifier and — only for non-canonical
                        // results — schedule the same async AI enrichment
                        // the OCR delivery branch uses.
                        ...(explicitCategory
                            ? { category: explicitCategory }
                            : { auto_classify: true }),
                    });
                    try {
                        const { data } = await api.post("/products", prodBody);
                        pid = data.id;
                    } catch (e) {
                        // Non-fatal — delivery item still accepts product_name
                        // without a product_id and the normal confirm path
                        // handles resolution via the existing matching
                        // pipeline.
                        pid = null;
                    }
                }
                resolved.push({ ...r, product_id: pid });
            }
            // 3. Create the delivery header. Status = needs_review
            //    (same as every other manual-origin delivery today).
            const { data: dres } = await api.post("/deliveries", {
                supplier_id: supplierId,
                delivery_date: deliveryDate,
                doc_number: docNumber || "",
                notes: notes || "",
                restaurant_id: needsExplicit ? restaurantId : null,
            });
            const deliveryId = dres.id;
            // 4. Add every row through the canonical item endpoint —
            //    pack_count/pack_size/pack_unit, product_id and VAT
            //    all flow through unchanged.
            for (const r of resolved) {
                const qty = Number(r.quantity) || 0;
                const price = Number(r.price) || 0;
                const vat = Number(r.vat) || 0;
                await api.post(`/deliveries/${deliveryId}/items`, {
                    product_name: r.name.trim(),
                    product_id: r.product_id || null,
                    quantity: qty,
                    unit: r.unit || "kpl",
                    price,
                    vat,
                    total: qty * price,
                    barcode: r.barcode || "",
                    notes: "",
                    source: "manual",
                    pack_count: r.pack_count === "" || r.pack_count == null
                        ? null : Number(r.pack_count),
                    pack_size: r.pack_size === "" || r.pack_size == null
                        ? null : Number(r.pack_size),
                    pack_unit: (r.pack_unit || "").trim() || null,
                    package: r.package || "",
                });
            }
            toast.success(
                t("deliveries.savedAsDraft", "Delivery saved as draft. Confirm it from the delivery detail to update stock."));
            onSavedAndOpen(deliveryId);
        } catch (err) {
            toast.error(apiErrorMessage(err)
                || t("deliveries.err.couldNotSaveManual", "Could not save manual delivery"));
        } finally { setSaving(false); }
    };

    return (
        <>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
             data-testid="wizard-step-manual">
            <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
                {t("deliveries.manualIntro", "Manual entry — no delivery note required. Save creates a draft; opening the delivery and pressing Confirm Delivery posts stock, totals and purchases just like any other delivery.")}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
                <SupplierSelector suppliers={suppliers}
                    supplier={supplier} onChange={setSupplier} />
                <LabeledInput label={t("deliveries.deliveryDate", "Delivery Date")} type="date" value={deliveryDate}
                    onChange={setDeliveryDate} testId="manual-date" />
                <LabeledInput label={t("deliveries.deliveryNoteNumber", "Delivery Note Number")} value={docNumber}
                    onChange={setDocNumber} testId="manual-doc-number" />
                <LabeledInput label={t("deliveries.invoiceNumber", "Invoice Number")} value={invoiceNumber}
                    onChange={setInvoiceNumber} testId="manual-invoice-number" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.notes", "Notes")}</label>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="manual-notes"
                    className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold">{t("deliveries.productsCount", { defaultValue: "Products ({{n}})", n: items.length })}</div>
                    <button data-testid="manual-add-row"
                        onClick={addRow}
                        className="h-9 px-3 inline-flex items-center gap-1 rounded-md border border-neutral-300 text-xs font-semibold hover:bg-neutral-50">
                        <Plus className="h-3.5 w-3.5" /> {t("deliveries.addProduct", "Add product")}
                    </button>
                </div>
                <ul className="space-y-2" data-testid="manual-items-list">
                    {items.map((it) => (
                        <ManualItemRow
                            key={it._key}
                            it={it}
                            catalogue={catalogue}
                            categories={categories}
                            onChange={(patch) => updateRow(it._key, patch)}
                            onRemove={() => removeRow(it._key)}
                            canRemove={items.length > 1}
                            onAddCategory={() => {
                                setNewCatForRow(it._key);
                                setNewCatName("");
                                setAddCatOpen(true);
                            }}
                        />
                    ))}
                </ul>
            </div>

            <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm tabular-nums"
                 data-testid="manual-totals">
                <span>{t("deliveries.exclVatCap", "Excl. VAT")} <b data-testid="manual-total-net">€ {totals.net.toFixed(2)}</b></span>
                <span className="text-neutral-500">·</span>
                <span>{t("deliveries.vat", "VAT")} <b data-testid="manual-total-vat">€ {totals.vat.toFixed(2)}</b></span>
                <span className="text-neutral-500">·</span>
                <span>{t("deliveries.inclVatCap", "Incl. VAT")} <b data-testid="manual-total-gross">€ {totals.gross.toFixed(2)}</b></span>
            </div>

            <div className="flex items-center justify-between pt-1">
                <button data-testid="manual-back"
                    onClick={onBack}
                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                    <ArrowLeft className="h-4 w-4" /> {t("deliveries.back", "Back")}
                </button>
                <button data-testid="manual-save-draft"
                    onClick={save} disabled={saving}
                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                    <Save className="h-4 w-4" />
                    {saving ? t("common.saving", "Saving…") : t("deliveries.saveAsDraft", "Save as Draft")}
                </button>
            </div>
        </div>

        {addCatOpen && (
            <Dialog open onOpenChange={(v) => !v && setAddCatOpen(false)}>
                <DialogContent data-testid="manual-add-category-dialog" className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t("deliveries.addCategory", "Add category")}</DialogTitle>
                        <DialogDescription>
                            {t("deliveries.addCategoryDesc", "Create a new product category for this company.")}
                        </DialogDescription>
                    </DialogHeader>
                    <LabeledInput label={t("deliveries.categoryName", "Category name")}
                        value={newCatName} onChange={setNewCatName}
                        testId="manual-add-category-name" />
                    <DialogFooter>
                        <button onClick={() => setAddCatOpen(false)}
                            className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">
                            {t("common.cancel", "Cancel")}
                        </button>
                        <button data-testid="manual-add-category-submit"
                            onClick={submitNewCategory} disabled={creatingCat}
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                            {creatingCat ? t("deliveries.creating", "Creating…") : t("deliveries.create", "Create")}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        )}
        </>
    );
}

function newManualRow() {
    return {
        _key: typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `mr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        product_id: null,
        name: "",
        quantity: "",
        unit: "kpl",
        price: "",
        vat: 13.5,
        barcode: "",
        pack_count: "",
        pack_size: "",
        pack_unit: "",
        package: "",
        category: "",
    };
}

function ManualItemRow({ it, catalogue, categories, onChange, onRemove,
                          canRemove, onAddCategory }) {
    const { t } = useTranslation();
    const [showPack, setShowPack] = useState(
        !!(it.pack_count || it.pack_size || it.pack_unit || it.package));
    // Resolve product_id when the typed name exactly matches an
    // existing product. Case-insensitive to mirror /products/lookup
    // semantics.
    const resolveProductByName = (nm) => {
        const needle = (nm || "").trim().toLowerCase();
        if (!needle) return null;
        return catalogue.find((p) =>
            (p.product_name || "").toLowerCase() === needle) || null;
    };
    const onNameChange = (nm) => {
        const match = resolveProductByName(nm);
        if (match) {
            onChange({
                name: match.product_name,
                product_id: match.id,
                unit: it.unit || match.unit || "kpl",
                vat: it.vat || match.vat || 13.5,
                category: it.category || match.category || "",
                barcode: it.barcode || match.barcode || "",
            });
        } else {
            onChange({ name: nm, product_id: null });
        }
    };
    const activeCategoryNames = (categories && categories.length > 0)
        ? categories.map((c) => c.name)
        : [];
    const isMatched = !!it.product_id;
    const listId = `manual-products-${it._key}`;

    return (
        <li className={`rounded-md border p-3 grid grid-cols-12 gap-2 items-start
                        ${isMatched ? "border-emerald-200 bg-emerald-50/40" :
                          "border-amber-200 bg-amber-50/40"}`}
            data-testid={`manual-item-${it._key}`}>
            <div className="col-span-12 sm:col-span-4">
                <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">
                    {t("deliveries.product", "Product")}
                </label>
                <input
                    data-testid={`manual-item-name-${it._key}`}
                    list={listId}
                    value={it.name}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder={t("deliveries.typeOrPickProduct", "Type or pick a product")}
                    className="mt-1 w-full h-9 px-2 border border-neutral-300 rounded-md text-sm" />
                <datalist id={listId}>
                    {catalogue.slice(0, 300).map((p) => (
                        <option key={p.id} value={p.product_name} />
                    ))}
                </datalist>
                <div className="text-[11px] mt-1">
                    {isMatched
                        ? <span className="text-emerald-700 font-semibold">{t("deliveries.existingProduct", "Existing product")}</span>
                        : it.name
                            ? <span className="text-amber-700 font-semibold">{t("deliveries.newProductOnSave", "New product — will be created on save")}</span>
                            : <span className="text-neutral-400">{t("deliveries.pickOrTypeName", "Pick an existing product or type a new name")}</span>}
                </div>
            </div>

            <div className="col-span-4 sm:col-span-1">
                <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">{t("deliveries.qtyShort", "Qty")}</label>
                <input type="number" step="0.01" min="0" value={it.quantity}
                    onChange={(e) => onChange({ quantity: e.target.value })}
                    data-testid={`manual-item-qty-${it._key}`}
                    className="mt-1 w-full h-9 px-2 border border-neutral-300 rounded-md text-sm" />
            </div>
            <div className="col-span-4 sm:col-span-1">
                <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">{t("deliveries.unit", "Unit")}</label>
                <input value={it.unit}
                    onChange={(e) => onChange({ unit: e.target.value })}
                    data-testid={`manual-item-unit-${it._key}`}
                    className="mt-1 w-full h-9 px-2 border border-neutral-300 rounded-md text-sm" />
            </div>
            <div className="col-span-4 sm:col-span-2">
                <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">
                    {t("deliveries.priceExclVat", "Price (excl. VAT)")}
                </label>
                <input type="number" step="0.01" min="0" value={it.price}
                    onChange={(e) => onChange({ price: e.target.value })}
                    data-testid={`manual-item-price-${it._key}`}
                    className="mt-1 w-full h-9 px-2 border border-neutral-300 rounded-md text-sm" />
            </div>
            <div className="col-span-4 sm:col-span-1">
                <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">{t("deliveries.fee.vatPct", "VAT %")}</label>
                <input type="number" step="0.5" min="0" value={it.vat}
                    onChange={(e) => onChange({ vat: e.target.value })}
                    data-testid={`manual-item-vat-${it._key}`}
                    className="mt-1 w-full h-9 px-2 border border-neutral-300 rounded-md text-sm" />
            </div>
            <div className="col-span-8 sm:col-span-3">
                <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">
                    {t("common.category", "Category")} {isMatched && <span className="text-neutral-400 normal-case">{t("deliveries.fromExistingProduct", "(from existing product)")}</span>}
                </label>
                <Select value={it.category || "__none__"}
                    onValueChange={(v) => {
                        if (v === "__add__") { onAddCategory(); return; }
                        onChange({ category: v === "__none__" ? "" : v });
                    }}
                    disabled={isMatched}>
                    <SelectTrigger className="h-9 mt-1" data-testid={`manual-item-category-${it._key}`}>
                        <SelectValue placeholder={t("deliveries.pickCategory", "Pick a category")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">— {t("deliveries.fee.none", "None")} —</SelectItem>
                        {activeCategoryNames.map((n) => (
                            <SelectItem key={n} value={n}>{n}</SelectItem>
                        ))}
                        {!isMatched && (
                            <SelectItem value="__add__" data-testid={`manual-item-category-add-${it._key}`}>
                                + {t("deliveries.addCategory", "Add category")}
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
            </div>

            <div className="col-span-12 flex items-center justify-between pt-1">
                <button data-testid={`manual-item-toggle-pack-${it._key}`}
                    onClick={() => setShowPack((v) => !v)}
                    className="text-xs text-neutral-500 hover:text-neutral-900 font-semibold">
                    {showPack ? `− ${t("deliveries.hidePackage", "Hide package")}` : `+ ${t("deliveries.addPackage", "Add package (count × size unit)")}`}
                </button>
                <div className="flex items-center gap-2">
                    <div className="text-[11px] tabular-nums text-neutral-600"
                         data-testid={`manual-item-line-total-${it._key}`}>
                        {t("deliveries.line", "Line:")} € {((Number(it.quantity) || 0) * (Number(it.price) || 0)).toFixed(2)}
                        <span className="ml-2">
                            {t("deliveries.incl", "Incl.")} € {(((Number(it.quantity) || 0) * (Number(it.price) || 0))
                                * (1 + (Number(it.vat) || 0) / 100)).toFixed(2)}
                        </span>
                    </div>
                    {canRemove && (
                        <button data-testid={`manual-item-remove-${it._key}`}
                            onClick={onRemove}
                            aria-label={t("deliveries.removeLine", "Remove line")}
                            className="h-8 w-8 grid place-items-center rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {showPack && (
                <div className="col-span-12 mt-1 pt-2 border-t border-dashed border-neutral-200 grid grid-cols-12 gap-2 items-end"
                     data-testid={`manual-item-pack-${it._key}`}>
                    <div className="col-span-4 sm:col-span-2">
                        <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">
                            {t("deliveries.packCountLabel", "Pack count")}
                        </label>
                        <input type="number" step="1" min="0" value={it.pack_count}
                            onChange={(e) => onChange({ pack_count: e.target.value })}
                            data-testid={`manual-item-pack-count-${it._key}`}
                            className="mt-1 w-full h-9 px-2 border border-neutral-300 rounded-md text-sm bg-white" />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                        <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">
                            {t("deliveries.packSizeLabel", "Pack size")}
                        </label>
                        <input type="number" step="0.001" min="0" value={it.pack_size}
                            onChange={(e) => onChange({ pack_size: e.target.value })}
                            data-testid={`manual-item-pack-size-${it._key}`}
                            className="mt-1 w-full h-9 px-2 border border-neutral-300 rounded-md text-sm bg-white" />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                        <label className="text-[11px] uppercase tracking-wide font-semibold text-neutral-600">
                            {t("deliveries.packUnitLabel", "Pack unit")}
                        </label>
                        <input value={it.pack_unit}
                            onChange={(e) => onChange({ pack_unit: e.target.value })}
                            data-testid={`manual-item-pack-unit-${it._key}`}
                            placeholder={t("deliveries.packUnitPlaceholder", "kg / l / pcs")}
                            className="mt-1 w-full h-9 px-2 border border-neutral-300 rounded-md text-sm bg-white" />
                    </div>
                    <div className="col-span-12 sm:col-span-6 text-[11px] text-neutral-600">
                        {(it.pack_count && it.pack_size && (it.pack_unit || "").trim()) ? (
                            <>{t("deliveries.receivedPerPackage", "Received per package line:")}&nbsp;
                                <b>{(Number(it.pack_count) * Number(it.pack_size)).toFixed(3)
                                    .replace(/\.?0+$/, "")}&nbsp;{it.pack_unit}</b></>
                        ) : (
                            <span className="text-neutral-400">
                                {t("deliveries.directQuantity", "Direct quantity:")} {Number(it.quantity) || 0} {it.unit}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </li>
    );
}


function StepDeliveryNote({ photos, onAddPhoto, onRemovePhoto, onMovePhoto, groupMode,
                            onGroupModeChange, phoneSeenRef, autoOpenPhone, onAutoPhoneOpened,
                            onDone, onManual }) {
    const { t } = useTranslation();
    const fileRef = useRef(null);
    const cameraRef = useRef(null);
    const scanRef = useRef(null);
    const { user, restaurantId } = useApp();
    const { needsExplicit, requireSelection } = useRestaurantScope();
    const { isFeatureEnabled } = useResolvedConfig();
    const companionEnabled = isFeatureEnabled("phone_companion_capture");
    const [phoneOpen, setPhoneOpen] = useState(false);
    const [phoneFetching, setPhoneFetching] = useState(false);
    // Best-effort mobile detection to prioritise "Take Photo" on phones.
    const isMobile = typeof navigator !== "undefined"
        && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const acceptFile = (f, capture = null) => {
        if (!f) return;
        const okType = /^(image\/(jpeg|png|jpg)|application\/pdf)$/i.test(f.type)
            || /\.(jpe?g|png|pdf)$/i.test(f.name);
        if (!okType) { toast.error(t("deliveries.err.fileType", "Only JPG, PNG or PDF are allowed")); return; }
        if (f.size > 20 * 1024 * 1024) { toast.error(t("deliveries.err.fileTooLarge", "File too large (max 20 MB)")); return; }
        onAddPhoto(f, capture);
    };

    // Multi-file selection support on desktop file picker. Each file
    // is validated and appended in the order the OS returned it — which
    // matches the operator's selection order and therefore preserves
    // capture intent.
    const acceptFiles = (fs) => {
        if (!fs || fs.length === 0) return;
        for (const f of fs) acceptFile(f);
    };

    // Camera availability: mobile browsers honour `<input capture>`
    // and open the rear camera reliably. Desktop browsers ignore
    // `capture` and silently fall back to the file picker — that is
    // exactly what the PO forbids ("Do NOT silently open the normal
    // file picker" §Issue 2). So on desktop we ALWAYS surface the toast
    // and let the operator use Connect Phone or Upload File instead.
    // We do NOT ship a bespoke desktop camera UI in this cycle.
    const openCamera = (which /* 'photo' | 'scan' */) => {
        if (isMobile) {
            (which === "scan" ? scanRef : cameraRef).current?.click();
            return;
        }
        toast.error(which === "scan"
            ? t("deliveries.err.scanMobileOnly", "Document scanning is available on mobile only. Use Connect Phone to scan from a phone, or Upload File.")
            : t("deliveries.err.cameraMobileOnly", "Camera capture is available on mobile only. Use Connect Phone to shoot from a phone, or Upload File."));
    };

    // capture_ids already received in this delivery (survives step remounts).
    const capturedIdsRef = phoneSeenRef;

    useEffect(() => {
        if (!autoOpenPhone) return;
        onAutoPhoneOpened();
        if (companionEnabled && !isMobile && requireSelection()) setPhoneOpen(true);
    }, [autoOpenPhone, onAutoPhoneOpened, companionEnabled, isMobile, requireSelection]);

    const handlePhoneCapture = async (payload, sessionId) => {
        if (payload?.kind !== "delivery") return;
        const cid = payload?.capture_id;
        if (!cid || !sessionId) return;
        if (capturedIdsRef.current.has(cid)) return;
        capturedIdsRef.current.add(cid);
        setPhoneFetching(true);
        try {
            const res = await api.get(
                `/companion/captures/${sessionId}/${cid}/file`,
                { responseType: "blob" });
            const blob = res.data;
            const filename = payload?.file_name || `phone-capture-${Date.now()}.jpg`;
            const type = payload?.file_mime || blob.type;
            const fetchedFile = new File([blob], filename, { type });
            acceptFile(fetchedFile, { session_id: sessionId, capture_id: cid });
            toast.success(t("deliveries.pageReceivedFromPhone", "Page received from phone"));
            // Do NOT close the modal — the operator may still be
            // capturing more pages from the phone.
        } catch (e) {
            capturedIdsRef.current.delete(cid);
            toast.error(t("deliveries.err.couldNotAttachPhone", "Could not attach photo from phone"));
        } finally { setPhoneFetching(false); }
    };

    const captureLabel = photos.length === 0 ? t("deliveries.takePhoto", "Take Photo") : t("deliveries.continueCapturing", "Continue Capturing");
    // Preserved for callers that reach this component via other flows.
    // Not rendered inside the Receive Delivery button row — the PO
    // asked to hide only the visible Take Photo / Scan Document
    // buttons while keeping their handlers and refs intact.
    const _takePhotoBtn = (
        <button key="take-photo" data-testid="wizard-take-photo"
            onClick={() => openCamera("photo")}
            className={`flex-1 min-w-[140px] h-16 inline-flex items-center justify-center gap-2 rounded-md text-sm font-semibold border ${isMobile ? "bg-neutral-900 text-white border-neutral-900 hover:bg-neutral-800" : "bg-white border-neutral-300 hover:bg-neutral-50"}`}>
            <Camera className="h-4 w-4" /> {captureLabel}
        </button>
    );
    const uploadBtn = (
        <button key="upload" data-testid="wizard-upload-file" onClick={() => fileRef.current?.click()}
            className={`flex-1 min-w-[140px] h-16 inline-flex items-center justify-center gap-2 rounded-md text-sm font-semibold border ${isMobile ? "bg-white border-neutral-300 hover:bg-neutral-50" : "bg-neutral-900 text-white border-neutral-900 hover:bg-neutral-800"}`}>
            <Upload className="h-4 w-4" /> {photos.length === 0 ? t("deliveries.uploadFile", "Upload File") : t("deliveries.uploadFiles", "Upload Files")}
        </button>
    );
    const _scanBtn = (
        <button key="scan" data-testid="wizard-scan-document"
            onClick={() => openCamera("scan")}
            className="flex-1 min-w-[140px] h-16 inline-flex items-center justify-center gap-2 rounded-md text-sm font-semibold border bg-white border-neutral-300 hover:bg-neutral-50">
            <ScanLine className="h-4 w-4" /> {t("deliveries.scanDocument", "Scan Document")}
        </button>
    );
    const connectPhoneBtn = companionEnabled && !isMobile && (
        <button key="connect-phone" data-testid="wizard-connect-phone"
            onClick={() => {
                if (!requireSelection()) return;
                setPhoneOpen(true);
            }}
            disabled={phoneFetching}
            className="flex-1 min-w-[140px] h-16 inline-flex items-center justify-center gap-2 rounded-md text-sm font-semibold border bg-white border-neutral-300 hover:bg-neutral-50 disabled:opacity-60">
            <Smartphone className="h-4 w-4" />
            {phoneFetching ? t("deliveries.attaching", "Attaching…")
                : photos.length > 0 ? t("deliveries.addPagesFromPhone", "Add pages from phone")
                : t("deliveries.connectPhone", "Connect Phone")}
        </button>
    );
    // Manual entry — one or more product rows without a delivery note.
    // Only exposed when no photos have been captured, matching the
    // "entry mode" semantics of Upload File / Connect Phone.
    const manualBtn = photos.length === 0 && (
        <button key="manual" data-testid="wizard-manual-delivery"
            onClick={() => onManual && onManual()}
            className="flex-1 min-w-[140px] h-16 inline-flex items-center justify-center gap-2 rounded-md text-sm font-semibold border bg-white border-neutral-300 hover:bg-neutral-50">
            <Plus className="h-4 w-4" /> {t("deliveries.manualDelivery", "Manual Delivery")}
        </button>
    );
    // takePhotoBtn / scanBtn remain defined above so their handlers,
    // refs and hidden file inputs stay wired for callers that reach
    // this component via other flows. They are intentionally omitted
    // from the Receive Delivery button row per PO instruction.

    return (
        <div className="space-y-4" data-testid="wizard-step-delivery-note">
            <div>
                <h3 className="text-lg font-display font-bold">{t("deliveries.deliveryNote", "Delivery Note")}</h3>
                <p className="text-sm text-neutral-500">
                    {t("deliveries.deliveryNoteIntro", "Upload a delivery note (photo or PDF), connect your phone, or enter the delivery manually without an attachment. For multi-page uploads, add every page and press Read pages.")}
                </p>
            </div>
            <div className="flex flex-wrap gap-2">
                {[uploadBtn, connectPhoneBtn, manualBtn]}
            </div>
            <input ref={fileRef} type="file" className="hidden" multiple
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                data-testid="wizard-file-input"
                onChange={(e) => { acceptFiles(e.target.files); e.target.value = ""; }} />
            <input ref={cameraRef} type="file" className="hidden"
                accept="image/*" capture="environment"
                data-testid="wizard-camera-input"
                onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ""; }} />
            <input ref={scanRef} type="file" className="hidden"
                accept="image/*,application/pdf" capture="environment"
                data-testid="wizard-scan-input"
                onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ""; }} />

            {companionEnabled && (
                <PhoneCompanionModal
                    open={phoneOpen}
                    mode="delivery"
                    restaurantId={needsExplicit ? restaurantId : undefined}
                    fresh
                    onCapture={handlePhoneCapture}
                    onFinish={() => toast.success(t("deliveries.phoneFinished", "Phone capture finished — check the pages and press Read pages."))}
                    onClose={() => setPhoneOpen(false)}
                />
            )}

            {photos.length > 0 ? (
                <PageList photos={photos} onRemove={onRemovePhoto} onMove={onMovePhoto} />
            ) : (
                <p className="text-xs text-neutral-500">
                    {t("deliveries.skipAttachHint", "You can also skip this step and attach the delivery note later from the delivery detail.")}
                </p>
            )}

            {photos.length > 1 && (
                <GroupingChoice value={groupMode} onChange={onGroupModeChange} />
            )}

            {photos.length > 0 && (
                <div className="flex items-center justify-between gap-3 pt-1">
                    <p className="text-xs font-semibold text-neutral-700" data-testid="wizard-doc-page-count">
                        {photos.length > 1 && groupMode === false
                            ? t("deliveries.countSeparate", { defaultValue: "{{count}} deliveries · 1 page each", count: photos.length })
                            : t("deliveries.countGrouped", { defaultValue: "1 delivery · {{count}} pages", count: photos.length })}
                    </p>
                    <button
                        data-testid="wizard-done-capture"
                        onClick={onDone}
                        disabled={photos.length > 1 && groupMode == null}
                        className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Check className="h-4 w-4" /> {t("deliveries.readPages", "Read pages")}
                    </button>
                </div>
            )}
            <p className="text-[11px] text-neutral-400">{t("deliveries.supportedFormats", "Supported: JPG, PNG, PDF · Max 20 MB per photo.")}</p>
        </div>
    );
}

function PageThumb({ file }) {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        if (!file || !(file.type || "").startsWith("image/")) return undefined;
        const u = URL.createObjectURL(file);
        setUrl(u);
        return () => URL.revokeObjectURL(u);
    }, [file]);
    return (
        <div className="h-14 w-11 shrink-0 rounded border border-neutral-200 bg-white overflow-hidden grid place-items-center">
            {url ? <img src={url} alt="" className="h-full w-full object-cover" />
                : <span className="text-[10px] font-bold text-neutral-500">PDF</span>}
        </div>
    );
}

function PageList({ photos, onRemove, onMove }) {
    const { t } = useTranslation();
    const iconBtn = "h-8 w-8 grid place-items-center rounded-md text-neutral-600 hover:bg-neutral-100 disabled:opacity-30";
    return (
        <ol className="space-y-2" data-testid="wizard-photos-list">
            {photos.map((p, idx) => (
                <li key={p.id} data-testid={`wizard-photo-${idx}`}
                    className="rounded-md border border-neutral-200 bg-neutral-50 px-2 py-2 flex items-center gap-3">
                    <PageThumb file={p.file} />
                    <div className="flex-1 text-sm min-w-0">
                        <div className="font-semibold text-neutral-900 truncate">
                            {t("deliveries.pageN", { defaultValue: "Page {{n}}", n: idx + 1 })} · {p.file?.name}
                        </div>
                        <div className="text-xs text-neutral-500">
                            {(p.file?.size / 1024).toFixed(1)} KB{p.capture ? ` · ${t("deliveries.fromPhone", "from phone")}` : ""}
                        </div>
                    </div>
                    <button data-testid={`wizard-move-up-${idx}`} disabled={idx === 0}
                        onClick={() => onMove(p.id, -1)} className={iconBtn}
                        aria-label={t("deliveries.moveUp", "Move up")}><ChevronUp className="h-4 w-4" /></button>
                    <button data-testid={`wizard-move-down-${idx}`} disabled={idx === photos.length - 1}
                        onClick={() => onMove(p.id, 1)} className={iconBtn}
                        aria-label={t("deliveries.moveDown", "Move down")}><ChevronDown className="h-4 w-4" /></button>
                    <button data-testid={`wizard-remove-photo-${idx}`} onClick={() => onRemove(p.id)}
                        className={`${iconBtn} hover:text-red-600`}
                        aria-label={t("deliveries.removePhotoN", { defaultValue: "Remove photo {{n}}", n: idx + 1 })}>
                        <XIcon className="h-4 w-4" />
                    </button>
                </li>
            ))}
        </ol>
    );
}

function GroupingChoice({ value, onChange }) {
    const { t } = useTranslation();
    const opt = (v, testId, label, hint) => (
        <label data-testid={testId}
            className={`flex-1 min-w-[200px] cursor-pointer rounded-md border px-3 py-2 text-sm ${value === v ? "border-neutral-900 bg-white" : "border-neutral-200 bg-neutral-50 hover:bg-neutral-100"}`}>
            <input type="radio" name="delivery-grouping" className="mr-2" checked={value === v}
                onChange={() => onChange(v)} data-testid={`${testId}-input`} />
            <span className="font-semibold">{label}</span>
            <span className="block text-xs text-neutral-500 mt-0.5">{hint}</span>
        </label>
    );
    return (
        <div data-testid="delivery-grouping-choice">
            <div className="text-xs font-semibold text-neutral-700 mb-1">
                {t("deliveries.groupingQuestion", "How should these pages be saved?")}
            </div>
            <div className="flex flex-wrap gap-2">
                {opt(true, "grouping-one-delivery", t("deliveries.groupOne", "All pages are one delivery"),
                    t("deliveries.groupOneHint", "One delivery note spread over several pages"))}
                {opt(false, "grouping-separate", t("deliveries.groupSeparate", "Each page is its own delivery"),
                    t("deliveries.groupSeparateHint", "Reviewed and confirmed one after another"))}
            </div>
        </div>
    );
}

// Per-photo processing step. Renders progress while AI extraction runs,
// and if any photo fails offers per-photo Retry / Remove and a global
// "Continue without failed photos" action that requires deliberate
// confirmation. Successful extractions are never discarded when
// retrying a single failure.
function PhotoProcessingStep({ photos, onRetry, onRemove, onBackToCapture,
                                onContinueWithoutFailures, ackContinueWithFailures }) {
    const { t } = useTranslation();
    const oks = photos.filter((p) => p.status === "ok").length;
    const failed = photos.filter((p) => p.status === "error");
    const pending = photos.filter((p) => p.status === "pending" || p.status === "reading");
    const inProgress = pending.length > 0;
    const [confirmContinue, setConfirmContinue] = useState(false);

    return (
        <div className="space-y-4" data-testid="wizard-step-reading">
            <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-neutral-900 grid place-items-center animate-pulse">
                    <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                    <div className="font-display text-lg font-bold">
                        {inProgress ? t("deliveries.readingNotes", "Reading delivery notes…") : t("deliveries.readingComplete", "Reading complete")}
                    </div>
                    <div className="text-xs text-neutral-500">
                        {t("deliveries.photosRead", { defaultValue: "{{ok}} of {{count}} photos read", ok: oks, count: photos.length })}
                        {failed.length > 0 && <> · <span className="text-amber-700 font-semibold">{t("deliveries.failedCount", { defaultValue: "{{count}} failed", count: failed.length })}</span></>}
                    </div>
                </div>
            </div>

            <ul className="space-y-2" data-testid="wizard-photo-status-list">
                {photos.map((p, idx) => {
                    const label = t("deliveries.photoN", { defaultValue: "Photo {{n}}", n: idx + 1 });
                    const filename = p.file?.name || "";
                    return (
                        <li
                            key={p.id}
                            data-testid={`wizard-photo-status-${idx}`}
                            data-photo-status={p.status}
                            className={`rounded-md border px-3 py-2 flex items-center gap-2 text-sm ${
                                p.status === "ok" ? "border-emerald-200 bg-emerald-50" :
                                p.status === "error" ? "border-amber-300 bg-amber-50" :
                                p.status === "reading" ? "border-neutral-300 bg-neutral-50" :
                                "border-neutral-200 bg-white"
                            }`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate">{label} · {filename}</div>
                                <div className="text-xs text-neutral-600">
                                    {p.status === "pending" && t("deliveries.waiting", "Waiting…")}
                                    {p.status === "reading" && (
                                        p.progress && p.progress.pages_total > 1
                                            ? t("deliveries.readingPage", { defaultValue: "Reading page {{done}} of {{total}}…", done: Math.max(1, p.progress.pages_done), total: p.progress.pages_total })
                                            : t("deliveries.readingWithAi", "Reading with AI…")
                                    )}
                                    {p.status === "ok" && <>{t("deliveries.readSuccess", { defaultValue: "Read successfully — {{count}} lines detected", count: p.extraction?.counts?.detected ?? 0 })}{p.partial && <span className="text-amber-700"> {t("deliveries.somePagesUnread", "(some pages could not be read)")}</span>}</>}
                                    {p.status === "error" && (
                                        <span className="text-amber-800">
                                            {t("deliveries.extractionFailed", "Extraction failed:")} {p.error || t("deliveries.unknownError", "unknown error")}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {p.status === "reading" && (
                                <span className="text-xs text-neutral-500" data-testid={`wizard-photo-progress-${idx}`}>
                                    {p.progress && p.progress.chunks_total > 1
                                        ? `${p.progress.chunks_done}/${p.progress.chunks_total}`
                                        : t("deliveries.reading", "Reading…")}
                                </span>
                            )}
                            {p.status === "ok" && (
                                <Check className="h-4 w-4 text-emerald-700" />
                            )}
                            {p.status === "error" && (
                                <div className="flex items-center gap-1">
                                    <button
                                        data-testid={`wizard-photo-retry-${idx}`}
                                        onClick={() => onRetry(p)}
                                        className="h-8 px-2 rounded-md border border-neutral-300 bg-white text-xs font-semibold hover:bg-neutral-50"
                                    >
                                        {t("deliveries.retry", "Retry")}
                                    </button>
                                    <button
                                        data-testid={`wizard-photo-remove-${idx}`}
                                        onClick={() => onRemove(p.id)}
                                        className="h-8 w-8 grid place-items-center rounded-md text-neutral-500 hover:text-red-600 hover:bg-red-50"
                                        aria-label={t("deliveries.removeLabel", { defaultValue: "Remove {{label}}", label })}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            {failed.length > 0 && !inProgress && !ackContinueWithFailures && (
                <div
                    data-testid="wizard-photo-error-panel"
                    className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-3 text-sm"
                >
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-700 shrink-0" />
                        <div>
                            <div className="font-semibold text-amber-900">
                                {t("deliveries.photosCouldNotBeRead", { defaultValue: "{{count}} photos could not be read", count: failed.length })}
                            </div>
                            <div className="text-xs text-amber-800/90 mt-0.5">
                                {t("deliveries.failedPhotosHint", { defaultValue: "Retry the failed photos, remove them, or go back to capture again. You may also continue without them — but a deliberate confirmation is required so nothing is skipped silently.", count: failed.length })}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            data-testid="wizard-back-to-capture"
                            onClick={onBackToCapture}
                            className="h-9 px-3 rounded-md border border-neutral-300 bg-white text-xs font-semibold hover:bg-neutral-50"
                        >
                            <ArrowLeft className="h-3.5 w-3.5 inline mr-1" /> {t("deliveries.backToCapture", "Back to capture")}
                        </button>
                        <button
                            data-testid="wizard-continue-without-failed"
                            onClick={() => setConfirmContinue(true)}
                            className="h-9 px-3 rounded-md bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800"
                        >
                            {t("deliveries.continueWithoutFailed", { defaultValue: "Continue without failed photos", count: failed.length })}
                        </button>
                    </div>
                </div>
            )}

            {confirmContinue && (
                <Dialog open onOpenChange={() => setConfirmContinue(false)}>
                    <DialogContent data-testid="wizard-continue-confirm-dialog">
                        <DialogHeader>
                            <DialogTitle>{t("deliveries.continueWithoutFailedQ", "Continue without failed photos?")}</DialogTitle>
                            <DialogDescription>
                                {t("deliveries.continueWithoutFailedBody", { defaultValue: "The delivery draft will be built from the {{count}} successfully-read photos. Any lines that were on the failed photos will not appear in the draft. The failed photos will still be attached to the delivery as evidence.", count: oks })}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="gap-2">
                            <button
                                data-testid="wizard-continue-confirm-cancel"
                                onClick={() => setConfirmContinue(false)}
                                className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold"
                            >
                                {t("common.cancel", "Cancel")}
                            </button>
                            <button
                                data-testid="wizard-continue-confirm-ok"
                                onClick={() => { setConfirmContinue(false); onContinueWithoutFailures(); }}
                                className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                            >
                                {t("deliveries.yesContinue", "Yes, continue")}
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

function DeliveryDetailDialog({ open, onClose, delivery, reload }) {
    const { t } = useTranslation();
    const fileRef = useRef(null);
    const cameraRef = useRef(null);
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(false);
    const emptyItem = { product_name: "", quantity: 1, unit: "kpl", price: 0, vat: 13.5, barcode: "", notes: "" };
    const [newItem, setNewItem] = useState(emptyItem);

    if (!delivery) return null;

    const onUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const form = new FormData();
        form.append("file", f);
        try {
            await api.post(`/deliveries/${delivery.id}/documents`, form,
                { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(t("deliveries.documentAttached", "Document attached"));
            reload();
        } catch (_err) {
            toast.error(t("deliveries.err.uploadFailed", "Upload failed"));
        } finally {
            e.target.value = "";
        }
    };

    const queueOcr = async () => {
        try {
            await api.post(`/deliveries/${delivery.id}/queue-ocr`);
            toast.success(t("deliveries.queuedForOcr", "Queued for OCR (processing will happen when AI is enabled)."));
            reload();
        } catch (err) {
            toast.error(apiErrorMessage(err) || t("deliveries.err.couldNotQueue", "Could not queue"));
        }
    };

    const addItem = async () => {
        if (!newItem.product_name.trim()) { toast.error(t("deliveries.err.productNameRequired", "Product name is required")); return; }
        setAdding(true);
        try {
            await api.post(`/deliveries/${delivery.id}/items`, {
                ...newItem,
                quantity: Number(newItem.quantity),
                price: Number(newItem.price),
                vat: Number(newItem.vat),
                total: Number(newItem.quantity) * Number(newItem.price),
            });
            setNewItem(emptyItem);
            reload();
        } finally { setAdding(false); }
    };

    const updateItem = async (itemId, patch) => {
        await api.patch(`/deliveries/${delivery.id}/items/${itemId}`, patch);
        reload();
    };

    const removeItem = async (itemId) => {
        await api.delete(`/deliveries/${delivery.id}/items/${itemId}`);
        reload();
    };

    const saveDraft = async () => {
        setBusy(true);
        try {
            await api.post(`/deliveries/${delivery.id}/save-draft`);
            toast.success(t("deliveries.savedDraft", "Saved as draft"));
            reload();
        } finally { setBusy(false); }
    };

    const markReviewed = async () => {
        setBusy(true);
        try {
            await api.post(`/deliveries/${delivery.id}/mark-reviewed`);
            toast.success(t("deliveries.markedReviewed", "Delivery marked as reviewed"));
            reload();
        } finally { setBusy(false); }
    };

    const confirmDelivery = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/deliveries/${delivery.id}/confirm`);
            if (data && Number.isFinite(data.stock_updated)) {
                const parts = [t("deliveries.stockAdded", { defaultValue: "{{count}} items added to stock", count: data.stock_updated })];
                if (data.products_created > 0) {
                    parts.push(t("deliveries.newProductsCount", { defaultValue: "{{count}} new products", count: data.products_created }));
                }
                if (data.products_matched > 0) {
                    parts.push(t("deliveries.matchedCount", { defaultValue: "{{count}} matched", count: data.products_matched }));
                }
                toast.success(t("deliveries.completedWithParts", { defaultValue: "Delivery completed — {{parts}}", parts: parts.join(" · ") }));
            } else {
                toast.success(t("deliveries.completedProductsUpdated", "Delivery completed. Products updated."));
            }
            onClose();        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail && typeof detail === "object" && Array.isArray(detail.items)) {
                const lines = detail.items
                    .map((it) => t("deliveries.err.missingLine", { defaultValue: "• {{name}}: missing {{fields}}", name: it.product_name, fields: it.missing.map((m) => t(`deliveries.field.${m.replace(" ", "_")}`, m)).join(", ") }))
                    .join("\n");
                toast.error(`${translateErrorText(detail.message)}\n${lines}`);
            } else {
                toast.error(apiErrorMessage(err) || t("deliveries.err.cannotComplete", "Cannot complete delivery"));
            }
        } finally { setBusy(false); }
    };

    // Historical recovery: re-run the exact same inventory-application
    // logic as confirm without touching the delivery's status or totals.
    // Backend idempotency prevents double stock — safe for any completed
    // or reviewed delivery.
    const applyToInventory = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/deliveries/${delivery.id}/apply-to-inventory`);
            if (data?.already_applied || (data && data.stock_updated === 0)) {
                toast(t("deliveries.inventoryAlreadyApplied", "Inventory already applied — no changes."));
            } else if (data && Number.isFinite(data.stock_updated)) {
                const parts = [t("deliveries.stockAdded", { defaultValue: "{{count}} items added to stock", count: data.stock_updated })];
                if (data.products_created > 0) {
                    parts.push(t("deliveries.newProductsCount", { defaultValue: "{{count}} new products", count: data.products_created }));
                }
                if (data.products_matched > 0) {
                    parts.push(t("deliveries.matchedCount", { defaultValue: "{{count}} matched", count: data.products_matched }));
                }
                toast.success(t("deliveries.inventoryUpdatedWithParts", { defaultValue: "Inventory updated — {{parts}}", parts: parts.join(" · ") }));
            } else {
                toast.success(t("deliveries.inventoryUpdated", "Inventory updated."));
            }
        } catch (e) {
            toast.error(apiErrorMessage(e) || t("deliveries.err.couldNotApply", "Could not apply to inventory"));
        } finally { setBusy(false); }
    };

    const totalAmount = (delivery.items || []).reduce(
        (s, i) => s + Number(i.total || (Number(i.quantity || 0) * Number(i.price || 0))), 0);
    const itemsValid = (delivery.items || []).length > 0 && (delivery.items || []).every(
        (i) => (i.product_name || "").trim() && Number(i.quantity) > 0 && (i.unit || "").trim());

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl" data-testid="delivery-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 flex-wrap">
                        {t("deliveries.detailTitle", { defaultValue: "Delivery — {{name}}", name: delivery.supplier?.name })}
                        <StatusBadge status={STATUS_TONE[delivery.status] || "pending"}>
                            {STATUS_LABEL[delivery.status] ? t(`deliveries.status.${delivery.status}`, STATUS_LABEL[delivery.status]) : delivery.status}
                        </StatusBadge>
                    </DialogTitle>
                    <DialogDescription>
                        {formatDateLong(delivery.delivery_date)}
                    </DialogDescription>
                </DialogHeader>

                {/* Message 476 · Delivery-fee breakdown — read-only once
                    the delivery is completed; drafts expose edit via
                    the Review surface. Legacy / absent fee ⇒ hidden. */}
                {(() => {
                    const f = delivery.delivery_fee;
                    if (!f || !f.source || f.source === "none") return null;
                    const src = f.source;
                    const tone = (
                        src === "extracted" ? "bg-emerald-100 text-emerald-800" :
                        src === "manual" ? "bg-sky-100 text-sky-800" :
                        src === "explicit_zero" ? "bg-neutral-100 text-neutral-700" :
                        "bg-amber-100 text-amber-900");
                    const label = (
                        src === "extracted" ? t("deliveries.fee.detectedOnInvoice", "Detected on invoice") :
                        src === "manual" ? t("deliveries.fee.manualEntry", "Manual entry") :
                        src === "explicit_zero" ? t("deliveries.fee.explicitZero", "Explicit zero") :
                        t("deliveries.fee.needsReview", "Needs review"));
                    return (
                        <section data-testid="delivery-detail-fee"
                                 className="rounded-lg border border-neutral-200 p-3 bg-white">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold">{t("deliveries.fee.title", "Delivery fee")}</div>
                                <span data-testid="delivery-detail-fee-source"
                                      className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${tone}`}>
                                    {label}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
                                <div><span className="text-neutral-500">{t("deliveries.fee.netEur", "NET €")}</span>
                                    <div data-testid="delivery-detail-fee-net"
                                         className="font-semibold">
                                        {f.net == null ? "—" : Number(f.net).toFixed(2)}
                                    </div></div>
                                <div><span className="text-neutral-500">{t("deliveries.fee.vatPct", "VAT %")}</span>
                                    <div data-testid="delivery-detail-fee-rate"
                                         className="font-semibold">
                                        {f.vat_rate == null ? "—" : `${f.vat_rate} %`}
                                    </div></div>
                                <div><span className="text-neutral-500">{t("deliveries.fee.vatEur", "VAT €")}</span>
                                    <div data-testid="delivery-detail-fee-vat"
                                         className="font-semibold">
                                        {f.vat == null ? "—" : Number(f.vat).toFixed(2)}
                                    </div></div>
                            </div>
                            {f.raw_text && (
                                <div className="text-[11px] text-neutral-500 mt-1 truncate">
                                    {t("deliveries.fee.fromDocument", "From document:")} "{f.raw_text}"
                                </div>
                            )}
                        </section>
                    );
                })()}

                {/* Documents (AI-ready) */}
                <section className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-sm font-semibold flex items-center gap-2">
                                <FileText className="h-4 w-4" /> {t("deliveries.noteDocuments", "Delivery Note Documents")}
                            </div>
                            <div className="text-xs text-neutral-500 mt-0.5">
                                {t("deliveries.photosStoredForOcr", "Photos & PDFs stored for future AI OCR.")}
                                <span className="ml-2 italic">{t("deliveries.ocrStatus", "OCR status:")} {t(`deliveries.ocr.${delivery.ocr_status || "not_processed"}`, delivery.ocr_status || "not_processed")}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <input type="file" ref={fileRef} className="hidden" data-testid="delivery-file-input"
                                accept="image/*,application/pdf" onChange={onUpload} />
                            {/*
                              Mobile-camera capture (Prompt 19). Same upload handler;
                              `capture="environment"` asks the browser to open the rear
                              camera on phones/tablets. Desktop browsers ignore the
                              attribute and fall back to a file picker — that is the
                              required manual fallback.
                            */}
                            <input type="file" ref={cameraRef} className="hidden" data-testid="delivery-camera-input"
                                accept="image/*" capture="environment" onChange={onUpload} />
                            <button
                                data-testid="take-photo-button"
                                onClick={() => cameraRef.current?.click()}
                                className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-xs font-semibold bg-white hover:bg-neutral-50"
                                title={t("deliveries.takePhotoTip", "Take photo with the device camera")}
                            >
                                <Camera className="h-3.5 w-3.5" /> {t("deliveries.photo", "Photo")}
                            </button>
                            <button
                                data-testid="upload-document-button"
                                onClick={() => fileRef.current?.click()}
                                className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-xs font-semibold bg-white hover:bg-neutral-50"
                            >
                                <Upload className="h-3.5 w-3.5" /> {t("deliveries.upload", "Upload")}
                            </button>
                            <button
                                data-testid="queue-ocr-button"
                                disabled={!delivery.documents?.length}
                                onClick={queueOcr}
                                className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-xs font-semibold bg-white hover:bg-neutral-50 disabled:opacity-50"
                                title={t("deliveries.queueOcrTip", "Queue for future AI OCR")}
                            >
                                <Sparkles className="h-3.5 w-3.5" /> {t("deliveries.queueOcr", "Queue for OCR")}
                            </button>
                            <button
                                data-testid="delete-delivery-button"
                                onClick={async () => {
                                    const isConfirmed = delivery.status === "completed";
                                    const msg = isConfirmed
                                        ? t("deliveries.confirmDeleteCompleted", "Deleting this delivery will reverse all inventory movements. Continue?")
                                        : t("deliveries.confirmDeleteDraft", "Delete this delivery? This cannot be undone.");
                                    if (!window.confirm(msg)) return;
                                    try {
                                        const params = isConfirmed ? { confirm_reversal: true } : {};
                                        await api.delete(`/deliveries/${delivery.id}`, { params });
                                        toast.success(isConfirmed ? t("deliveries.deletedReversed", "Delivery deleted and stock reversed") : t("deliveries.deleted", "Delivery deleted"));
                                        onClose(); reload();
                                    } catch (e) {
                                        toast.error(apiErrorMessage(e) || t("deliveries.err.deleteFailed", "Delete failed"));
                                    }
                                }}
                                className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-rose-200 text-xs font-semibold text-rose-700 bg-white hover:bg-rose-50"
                            >
                                <Trash2 className="h-3.5 w-3.5" /> {t("common.delete", "Delete")}
                            </button>
                        </div>
                    </div>
                    {delivery.documents?.length > 0 && (
                        <ul className="mt-3 space-y-1" data-testid="delivery-documents-list">
                            {delivery.documents.map((d) => (
                                <li key={d.id}
                                    data-testid={`delivery-document-${d.id}`}
                                    className="text-xs text-neutral-700 flex items-center gap-2 border border-neutral-200 rounded-md px-2 py-1.5 bg-white">
                                    <FileText className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate flex-1">{d.filename}</span>
                                    <span className="text-neutral-400">{(d.size / 1024).toFixed(1)} KB</span>
                                    <a href={d.url} target="_blank" rel="noreferrer"
                                        data-testid={`delivery-document-view-${d.id}`}
                                        className="h-7 px-2 inline-flex items-center gap-1 rounded border border-neutral-200 hover:bg-neutral-50">
                                        <Eye className="h-3 w-3" /> {t("deliveries.view", "View")}
                                    </a>
                                    <a href={d.url} download={d.filename}
                                        data-testid={`delivery-document-download-${d.id}`}
                                        className="h-7 px-2 inline-flex items-center gap-1 rounded border border-neutral-200 hover:bg-neutral-50">
                                        <Upload className="h-3 w-3 rotate-180" /> {t("deliveries.download", "Download")}
                                    </a>
                                    <button
                                        data-testid={`delivery-document-replace-${d.id}`}
                                        onClick={() => fileRef.current?.click()}
                                        className="h-7 px-2 inline-flex items-center gap-1 rounded border border-neutral-200 hover:bg-neutral-50">
                                        <Upload className="h-3 w-3" /> {t("deliveries.replace", "Replace")}
                                    </button>
                                    {delivery.status !== "completed" && (
                                        <button
                                            data-testid={`delivery-document-delete-${d.id}`}
                                            aria-label={t("deliveries.confirmDeleteNote", "Delete this delivery note?")}
                                            onClick={async () => {
                                                if (!window.confirm(t("deliveries.confirmDeleteNote", "Delete this delivery note?"))) return;
                                                try {
                                                    await api.delete(`/deliveries/${delivery.id}/documents/${d.id}`);
                                                    toast.success(t("deliveries.documentDeleted", "Document deleted"));
                                                    reload();
                                                } catch { toast.error(t("deliveries.err.couldNotDelete", "Could not delete")); }
                                            }}
                                            className="h-7 px-2 inline-flex items-center gap-1 rounded border border-neutral-200 hover:bg-red-50 hover:text-red-600">
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Items */}
                <section className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold">{t("deliveries.itemsReceived", "Items received")}</div>
                        <div className="text-xs text-neutral-500">{t("deliveries.col.total", "Total")}: € {totalAmount.toFixed(2)}</div>
                    </div>
                    <div className="kop-card overflow-x-auto">
                        <table className="w-full text-sm" data-testid="delivery-items-table">
                            <thead className="bg-neutral-50">
                                <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                    <th className="px-3 py-2 font-semibold">{t("deliveries.product", "Product")}</th>
                                    <th className="px-3 py-2 font-semibold w-20">{t("deliveries.qtyShort", "Qty")}</th>
                                    <th className="px-3 py-2 font-semibold w-20">{t("deliveries.unit", "Unit")}</th>
                                    <th className="px-3 py-2 font-semibold w-24">{t("deliveries.price", "Price")}</th>
                                    <th className="px-3 py-2 font-semibold w-20">{t("deliveries.vat", "VAT")}</th>
                                    <th className="px-3 py-2 font-semibold w-28">{t("deliveries.col.total", "Total")}</th>
                                    <th className="px-3 py-2 font-semibold w-28">{t("deliveries.barcode", "Barcode")}</th>
                                    <th className="px-3 py-2 font-semibold w-16">{t("deliveries.source", "Src")}</th>
                                    <th className="px-3 py-2 w-8"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {(delivery.items || []).map((it) => (
                                    <EditableItemRow
                                        key={it.id}
                                        item={it}
                                        onChange={(patch) => updateItem(it.id, patch)}
                                        onRemove={() => removeItem(it.id)}
                                    />
                                ))}
                                {(!delivery.items || delivery.items.length === 0) && (
                                    <tr>
                                        <td colSpan={9} className="px-3 py-4 text-sm text-neutral-400 italic">
                                            {t("deliveries.noItemsYet", "No items yet. Add the first product below.")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Add row */}
                    <div className="mt-3 grid grid-cols-12 gap-2">
                        <input data-testid="item-product-name" placeholder={`${t("deliveries.product", "Product")} *`} value={newItem.product_name}
                            onChange={(e) => setNewItem({ ...newItem, product_name: e.target.value })}
                            className="col-span-4 h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                        <input type="number" placeholder={`${t("deliveries.qtyShort", "Qty")} *`} aria-label={t("deliveries.qty", "Quantity")} value={newItem.quantity}
                            onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                            className="col-span-1 h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                        <input placeholder={`${t("deliveries.unit", "Unit")} *`} aria-label={t("deliveries.unit", "Unit")} value={newItem.unit}
                            onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                            className="col-span-1 h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                        <input type="number" placeholder={t("deliveries.price", "Price")} aria-label={t("deliveries.price", "Price")} value={newItem.price}
                            onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                            className="col-span-2 h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                        <input type="number" placeholder={t("deliveries.vat", "VAT")} aria-label={t("deliveries.vat", "VAT")} value={newItem.vat}
                            onChange={(e) => setNewItem({ ...newItem, vat: e.target.value })}
                            className="col-span-1 h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                        <input placeholder={t("deliveries.barcode", "Barcode")} aria-label={t("deliveries.barcode", "Barcode")} value={newItem.barcode}
                            onChange={(e) => setNewItem({ ...newItem, barcode: e.target.value })}
                            className="col-span-2 h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                        <button
                            data-testid="add-delivery-item"
                            aria-label={t("deliveries.addProduct", "Add product")}
                            onClick={addItem} disabled={adding}
                            className="col-span-1 h-10 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
                            +
                        </button>
                    </div>
                    <p className="text-[11px] text-neutral-400 mt-1">* {t("deliveries.requiredFieldsHint", "Product name, quantity and unit are required to complete the delivery.")}</p>
                </section>

                <DialogFooter className="gap-2">
                    <button data-testid="dialog-close-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.close", "Close")}</button>
                    {(delivery.status === "completed" || delivery.status === "reviewed") && (
                        <button
                            data-testid="apply-to-inventory"
                            onClick={applyToInventory}
                            disabled={busy}
                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-emerald-200 text-emerald-800 bg-emerald-50 text-sm font-semibold hover:bg-emerald-100 disabled:opacity-60"
                            title={t("deliveries.applyTip", "Historical recovery — safe to click even if already applied")}
                        >
                            <Boxes className="h-4 w-4" /> {t("deliveries.applyToInventory", "Apply to Inventory")}
                        </button>
                    )}
                    {delivery.status !== "completed" && (
                        <>
                            <button data-testid="save-draft-button" onClick={saveDraft} disabled={busy}
                                className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50 disabled:opacity-60">
                                <Save className="h-4 w-4" /> {t("deliveries.saveDraft", "Save draft")}
                            </button>
                            <button data-testid="mark-reviewed-button" onClick={markReviewed} disabled={busy || !itemsValid}
                                className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50 disabled:opacity-60"
                                title={!itemsValid ? t("deliveries.allItemsNeedFields", "All items need product name, quantity and unit") : ""}>
                                <Eye className="h-4 w-4" /> {t("deliveries.markReviewed", "Mark reviewed")}
                            </button>
                            <button data-testid="confirm-delivery"
                                onClick={confirmDelivery} disabled={busy || !itemsValid}
                                className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
                                title={!itemsValid ? t("deliveries.fixMissingFirst", "Fix missing required fields first") : ""}>
                                <Check className="h-4 w-4" /> {t("deliveries.completeDelivery", "Complete Delivery")}
                            </button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditableItemRow({ item, onChange, onRemove }) {
    const { t } = useTranslation();
    const [local, setLocal] = useState({
        product_name: item.product_name || "",
        quantity: item.quantity ?? 0,
        unit: item.unit || "kpl",
        price: item.price ?? 0,
        vat: item.vat ?? 13.5,
        barcode: item.barcode || "",
        // 2026-02 · packaging recall: EditableItemRow now surfaces
        // pack_size + pack_unit so the chef can see (and edit) the
        // supplier-specific packaging when it exists on this row —
        // either persisted by the invoice OR recovered by the server
        // via `pack_recovered_from_known_pack` on GET /deliveries/:id.
        // pack_count remains derived from `quantity` at write time
        // (see confirm_delivery); the UI never asks for it here.
        pack_size: item.pack_size ?? "",
        pack_unit: item.pack_unit || "",
    });

    // Reset local state when the item itself changes from the server.
    const itemRef = useLatest(item);
    useEffect(() => {
        const it = itemRef.current;
        setLocal({
            product_name: it.product_name || "",
            quantity: it.quantity ?? 0,
            unit: it.unit || "kpl",
            price: it.price ?? 0,
            vat: it.vat ?? 13.5,
            barcode: it.barcode || "",
            pack_size: it.pack_size ?? "",
            pack_unit: it.pack_unit || "",
        });
    }, [item.id, item.updated_at, item.pack_size, item.pack_unit, itemRef]);

    const commit = (field) => {
        const value = local[field];
        if (item[field] === value) return;
        const patch = { [field]: value };
        if (field === "quantity" || field === "price") {
            patch[field] = Number(value);
        }
        if (field === "pack_size") {
            patch[field] = value === "" ? null : Number(value);
        }
        if (field === "pack_unit") {
            patch[field] = value ? String(value).trim() : null;
        }
        onChange(patch);
    };

    const missingName = !local.product_name.trim();
    const missingQty = !(Number(local.quantity) > 0);
    const missingUnit = !local.unit.trim();
    const totalDisplay = Number(local.quantity || 0) * Number(local.price || 0);

    return (
        <>
        <tr data-testid={`delivery-item-row-${item.id}`}>
            <td className="px-3 py-2">
                <input
                    data-testid={`item-edit-name-${item.id}`}
                    aria-label={t("deliveries.product", "Product")}
                    value={local.product_name}
                    onChange={(e) => setLocal({ ...local, product_name: e.target.value })}
                    onBlur={() => commit("product_name")}
                    className={`w-full h-9 px-2 border rounded text-sm ${missingName ? "border-red-300 bg-red-50" : "border-transparent hover:border-neutral-200"}`}
                />
            </td>
            <td className="px-3 py-2">
                <input type="number" data-testid={`item-edit-qty-${item.id}`}
                    value={local.quantity}
                    onChange={(e) => setLocal({ ...local, quantity: e.target.value })}
                    onBlur={() => commit("quantity")}
                    className={`w-full h-9 px-2 border rounded text-sm ${missingQty ? "border-red-300 bg-red-50" : "border-transparent hover:border-neutral-200"}`} />
            </td>
            <td className="px-3 py-2">
                <input data-testid={`item-edit-unit-${item.id}`} value={local.unit}
                    onChange={(e) => setLocal({ ...local, unit: e.target.value })}
                    onBlur={() => commit("unit")}
                    className={`w-full h-9 px-2 border rounded text-sm ${missingUnit ? "border-red-300 bg-red-50" : "border-transparent hover:border-neutral-200"}`} />
            </td>
            <td className="px-3 py-2">
                <input type="number" data-testid={`item-edit-price-${item.id}`} value={local.price}
                    onChange={(e) => setLocal({ ...local, price: e.target.value })}
                    onBlur={() => commit("price")}
                    className="w-full h-9 px-2 border border-transparent hover:border-neutral-200 rounded text-sm" />
            </td>
            <td className="px-3 py-2">
                <input type="number" value={local.vat} aria-label={t("deliveries.vatPct", "VAT %")}
                    onChange={(e) => setLocal({ ...local, vat: e.target.value })}
                    onBlur={() => commit("vat")}
                    className="w-full h-9 px-2 border border-transparent hover:border-neutral-200 rounded text-sm" />
            </td>
            <td className="px-3 py-2 text-neutral-700">€ {totalDisplay.toFixed(2)}</td>
            <td className="px-3 py-2">
                <input value={local.barcode} aria-label={t("deliveries.barcode", "Barcode")}
                    onChange={(e) => setLocal({ ...local, barcode: e.target.value })}
                    onBlur={() => commit("barcode")}
                    className="w-full h-9 px-2 border border-transparent hover:border-neutral-200 rounded text-sm" />
            </td>
            <td className="px-3 py-2 text-xs text-neutral-500 capitalize">{t(`deliveries.itemSource.${item.source || "manual"}`, item.source || "manual")}</td>
            <td className="px-3 py-2 text-right">
                <button data-testid={`remove-item-${item.id}`} onClick={onRemove}
                    aria-label={t("deliveries.removeLine", "Remove line")}
                    className="h-8 w-8 grid place-items-center rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                </button>
            </td>
        </tr>
        {/* Packaging recall sub-row. Rendered whenever the row has a
            pack_size or pack_unit (from the invoice OR restored from
            products.supplier_skus[].known_pack on GET). The chef can
            override in place; leaving them empty preserves the direct
            unit/quantity semantics for legacy lines. */}
        {(local.pack_size !== "" || (local.pack_unit || "").trim()
          || item.pack_recovered_from_known_pack) && (
        <tr data-testid={`delivery-item-pack-row-${item.id}`}
            className="bg-neutral-50/70 border-t-0">
            <td colSpan={2} className="px-3 pb-2 pt-1 text-[11px] uppercase tracking-wide text-neutral-500">
                {t("deliveries.package", "Package")}
                {item.pack_recovered_from_known_pack && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold"
                        data-testid={`item-pack-recovered-${item.id}`}
                        title={t("deliveries.recoveredTip", "Recovered from this supplier's last confirmed delivery. Override to record a different pack.")}>
                        {t("deliveries.recovered", "Recovered")}
                    </span>
                )}
            </td>
            <td colSpan={2} className="px-3 pb-2 pt-1">
                <input type="number" step="0.001" min="0"
                    data-testid={`item-edit-pack-size-${item.id}`}
                    value={local.pack_size}
                    placeholder={t("deliveries.packSize", "size")}
                    onChange={(e) => setLocal({ ...local, pack_size: e.target.value })}
                    onBlur={() => commit("pack_size")}
                    className="w-full h-8 px-2 border border-neutral-200 rounded text-sm bg-white" />
            </td>
            <td colSpan={2} className="px-3 pb-2 pt-1">
                <input
                    data-testid={`item-edit-pack-unit-${item.id}`}
                    value={local.pack_unit}
                    placeholder={t("deliveries.packUnitExample", "unit (l, kg, …)")}
                    onChange={(e) => setLocal({ ...local, pack_unit: e.target.value })}
                    onBlur={() => commit("pack_unit")}
                    className="w-full h-8 px-2 border border-neutral-200 rounded text-sm bg-white" />
            </td>
            <td colSpan={3} className="px-3 pb-2 pt-1 text-[11px] text-neutral-600 tabular-nums"
                data-testid={`item-pack-derived-${item.id}`}>
                {Number(local.pack_size) > 0 && (local.pack_unit || "").trim() ? (
                    <>
                        {t("deliveries.received", "Received:")} <b>{(Number(local.quantity) * Number(local.pack_size)).toFixed(3).replace(/\.?0+$/, "")} {local.pack_unit}</b>
                        {Number(local.price) > 0 && Number(local.pack_size) > 0 && (
                            <span className="ml-2">· €{(Number(local.price) / Number(local.pack_size)).toFixed(2)}/{local.pack_unit}</span>
                        )}
                    </>
                ) : (
                    <span className="text-neutral-400">{t("deliveries.direct", "Direct:")} {Number(local.quantity) || 0} {local.unit}</span>
                )}
            </td>
        </tr>
        )}
        </>
    );
}
